import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeaders, getUser } from '../utils/auth';
import Sidebar from '../components/Sidebar';
import { ArrowLeft, ChevronRight, ChevronLeft, Save, AlertCircle, Plus, Trash2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

// Import validasi dan komponen baru
import { validatePTWForm, validateAllHighRiskForms, validateClosing } from '../utils/validation';
import { ValidationErrorSummary } from '../components/FormField';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PTWForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [picUsers, setPicUsers] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  // State untuk data tensi pekerja (untuk Working at Height)
  const [tensiPekerja, setTensiPekerja] = useState([]);
  const [hazardControlsTable, setHazardControlsTable] = useState([]);
  const [ventilasiData, setVentilasiData] = useState({
  cukup: null,      // null, 'Ya', atau 'Tidak'
  jenis: null       // null, 'Mechanical', atau 'Natural'
});
  const [permitReview, setPermitReview] = useState({
  monitoring_udara: null,      // 'Ya' atau 'Tidak'
  respirator_diperlukan: null, // 'Ya' atau 'Tidak'
  hazard_controls_ppe: null,   // 'Ya' atau 'Tidak'
  plant_safety_approval: null, // 'Ya' atau 'Tidak'
  monitoring_berkelanjutan: null // 'Ya' atau 'Tidak'
});

// 🔴 STEP 1: State untuk Section 13 - Tabel Peralatan
const [peralatanTable, setPeralatanTable] = useState([
  { id: 1, model: '', serial_number: '', tanggal_kalibrasi: '', pemilik: '' }
]);

// 🔴 STEP 1: State untuk Section 13 - Pertanyaan Monitoring Udara
const [monitoringUdaraTerusMenerus, setMonitoringUdaraTerusMenerus] = useState(null); // 'Ya' atau 'Tidak'

// 🔴 STEP 1: State untuk Section 13 - Tabel Pengukuran Udara
const [pengukuranUdaraTable, setPengukuranUdaraTable] = useState([
  { id: 1, tanggal: '', waktu: '', isolasi: '', oksigen: '', combustible: '', lel: '', h2s: '', co: '', toxic: '' }
]);

// 🔴 STEP 1: State untuk Section 14 - Tabel Entrants & Standby
const [entrantsTable, setEntrantsTable] = useState([
  { id: 1, nama_entrants: '',  nama_standby: '' }
]);

// 🔴 STEP 1: State untuk Section 14 - Emergency Procedure
const [emergencyProcedure, setEmergencyProcedure] = useState(null); // 'Ya', 'Tidak', 'Perlu Briefing'

// 🔴 STEP 1: State untuk Section 15 - QSHE Procedure
const [qsheProcedure, setQsheProcedure] = useState({
  guidelines: [],        // array: 'ULI', 'OSHA', 'HSE', 'Depnaker Indonesia', 'Lainnya'
  nama_procedure: '',
  risk_assessment: null  // 'RA yang sudah ada', 'RA Tambahan', 'RA yang dibuat sebelum pekerjaan'
});
  const [isEditMode, setIsEditMode] = useState(false);
  const [existingPTW, setExistingPTW] = useState(null);
  
  // Option B Flow: Track PTW creation for high-risk forms
  const [createdPTWId, setCreatedPTWId] = useState(null);
  const [isFillingHighRiskForms, setIsFillingHighRiskForms] = useState(false);
  
  // Check if we're in high-risk forms mode from URL
  const isHighRiskFormsRoute = location.pathname.includes('/high-risk-forms');
  
  // Closing mode
  const isClosingRoute = location.pathname.includes('/closing');
  const [isClosingMode, setIsClosingMode] = useState(false);
  const [closingData, setClosingData] = useState({
    closing_checklist: {},
    closing_disclaimer_accepted: false,
    closing_notes: ''
  });

  // Closing checklist items
  const closingChecklistItems = [
    { key: 'peralatan_dikembalikan', label: 'Peralatan dipakai sudah dikembalikan ke tempatnya' },
    { key: 'peralatan_off', label: 'Peralatan yang telah dipakai sudah dalam keadaan OFF' },
    { key: 'area_aman', label: 'Area dipastikan aman dan sudah dikembalikan ke kondisi awal' },
    { key: 'safety_terpasang', label: 'Semua peralatan safety telah terpasang kembali' },
    { key: 'pekerja_ditarik', label: 'Semua tenaga kerja yang melaksanakan pekerjaan telah ditarik' },
    { key: 'sampah_dikumpulkan', label: 'Sampah / potongan besi / kayu sudah dikumpulkan pada tempatnya yang sudah ditentukan' },
    { key: 'limbah_b3', label: 'Limbah B3 (Cat, Oli, dan sebagainya) sudah dikumpulkan di tempat penampungan limbah B3 yang sudah ditentukan' },
    { key: 'area_bersih', label: 'Area telah di bersihkan dan steril kembali' },
    { key: 'peralatan_bersih', label: 'Peralatan telah dibersihkan dan bebas dari kontaminasi' },
    { key: 'passivation', label: 'Prosedur Passivation telah dilakukan sesuai dengan standar yang berlaku' },
    { key: 'trial_dilakukan', label: 'Trial telah dilakukan untuk memastikan quality defect pada produk tidak terjadi' },
    { key: 'isolasi_dibuka', label: 'Isolasi area kerja dari area hygiene telah dibuka kembali' },
    
  { key: 'guard_terpasang', label: 'Seluruh proteksi telah terpasang kembali' }
  
  ]; 

  const [formData, setFormData] = useState({
    lokasi_pekerjaan: '',
    nama_pekerja: [''],
    deskripsi_pekerjaan: '',
    mobile_phone_1: '',
    mobile_phone_2: '',
    nomor_po: '',
    tanggal_mulai: '',
    high_risk_activities: [],
    jsa_table: [{ steps_of_activity: '', hazard_reaction: '', safe_plan: '', resources: '' }],
    potensi_bahaya: [],
    risiko_lalu_lintas: {},
    apd_digunakan: [],
    personal_hygiene: {},
    isolasi_listrik: { dilakukan: false, nama_nomor_panel: '', cara_mengisolasi: [], cara_mengisolasi_lainnya: '' },
    isolasi_utilitas: { dilakukan: false, jenis_utilitas: [], jenis_utilitas_lainnya: '', cara_mengisolasi: [], cara_mengisolasi_lainnya: '',
    amonia_jenis_pekerjaan: '',
    amonia_lokasi: '',
    amonia_nama_peralatan: '',
    amonia_pemilik_area: '',
    amonia_loto_person: '',
    amonia_uraian_pekerjaan: '',
    amonia_jenis_energi: '',
    amonia_titik_isolasi: '',
    amonia_metode_isolasi: '',
    amonia_peralatan_lock: '',
    amonia_apd_helmet: false,
    amonia_apd_sarung_tangan: false,
    amonia_apd_respirator: false,
    amonia_apd_lainnya: ''
     },
    isolasi_proses: { dilakukan: false, peralatan: '', cara_mengisolasi: [], cara_mengisolasi_lainnya: '' },
    daftar_pencegahan_umum: [],
    daftar_pencegahan_penggalian: [],
    daftar_pencegahan_lainnya: [],
    pic_id: '',
    high_risk_forms: {
      working_at_height: null,
      electric_medium_voltage: null,
      hot_work: null,
      confined_space: null
    }
  });

  const steps = isClosingMode
    ? ['Closing']
    : isFillingHighRiskForms 
    ? ['High Risk Forms'] 
    : ['Info Umum', 'JSA & High Risk', 'Bahaya & APD', 'Isolasi', 'Pencegahan', 'Review'];

  const highRiskOptions = [
    'Working at Height',
    'Hot Work',
    'Confined Space',
    'Electric Medium Voltage'
  ];

  const potensiBahayaOptions = [
    'Bahaya Kimia', 'Bahaya Kebakaran', 'Bahaya Ledakan', 'Bahaya Gas bertekanan',
    'Bahaya Bahan Panas', 'Steam / Uap panas', 'Bahaya Udara bertekanan', 'Debu / Uap',
    'Tekanan Tinggi / Rendah', 'Temperatur tinggi / Rendah', 'Listrik Menyala',
    'Akses Masuk tidak Aman', 'Memindahkan Mesin', 'Alat Menyala Otomatis',
    'Bahaya Lalu Lintas', 'Bekerja Sendiri', 'Kekurangan Oksigen', 'Kebakaran kabel',
    'Kebakaran Pipa', 'Pencemaran Lingkungan', 'Tertimpa Barang', 'Cuaca Hujan',
    'Kebisingan', 'Benda Tajam', 'Binatang'
  ];

  const risikoLaluLintasOptions = [
    'Kontaminasi Debu', 'Kontaminasi Mikro', 'Kontaminasi Benda Asing',
    'Kontaminasi Chemical', 'Packaging Tergores', 'Packaging Penyok', 'Packaging Bocor'
  ];

  const apdOptions = [
    'Helm', 'Sepatu Safety', 'Sarung Tangan', 'Kacamata Pelindung', 'Face Shield',
    'Protective Clothing', 'Apron', 'Ear Plug', 'Ear Muffs', 'Masker Debu',
    'Respirator Gas Mask', 'SCBA', 'Safety Belt / Harness', 'Safety Net / Terpal', 'Lain-lain'
  ];

  const personalHygieneOptions = [
    'Hair Cap', 'Masker', 'Kuku tidak panjang', 'Tidak memakai Perhiasan & Jam Tangan',
    'Wearpack Bersih', 'Peralatan Kerja Bersih', 'Tidak Ada Luka Terbuka',
    'Tidak Memiliki Penyakit Menular'
  ];

  const pencegahanUmumOptions = [
    'Telah Dicek Pemilik Lokasi', 'Safety Line di area Kerja', 'Papan Peringatan',
    'Pemasangan Safety Net/Terpal', 'Alat Pengangkat Tersertifikasi',
    'Operator Alat Pengangkat tersertifikasi', 'Disediakan Pengawasan di site',
    'Instalasi sesuai Hygenic Standar', 'Menggunakan Sanitary Equipment',
    'Melakukan Pembersihan / Cleaning', 'Menggunakan Disinfectant',
    'Area kerja tetap dijaga bersih & Kering', 'Isolasi Area kerja dan area Hygiene',
    'Peralatan kerja bersih & Steril', 'Cegah Kontak langsung dengan produk', 'Lainnya'
  ];

  useEffect(() => {
    fetchUsers();
    if (id) {
      if (isClosingRoute) {
        // Coming from /ptw/:id/closing - load PTW for closing
        fetchPTWForClosing();
      } else if (isHighRiskFormsRoute) {
        // Coming from /ptw/:id/high-risk-forms - load PTW and switch to high-risk mode
        fetchPTWForHighRiskForms();
      } else {
        fetchPTWForEdit();
      }
    }
  }, [id, isHighRiskFormsRoute, isClosingRoute]);

  const fetchPTWForClosing = async () => {
    try {
      const res = await axios.get(`${API}/ptw/${id}`, { headers: getAuthHeaders() });
      const ptw = res.data;
      
      if (ptw.status !== 'waiting_closing') {
        toast.error('PTW ini tidak dalam status menunggu closing');
        navigate(`/ptw/${id}`);
        return;
      }
      
      setExistingPTW(ptw);
      setIsClosingMode(true);
      
      toast.success('Silakan isi checklist closing');
    } catch (error) {
      console.error('Error fetching PTW:', error);
      toast.error('Gagal memuat data PTW');
      navigate(`/ptw/${id}`);
    }
  };

  const fetchPTWForHighRiskForms = async () => {
    try {
      const res = await axios.get(`${API}/ptw/${id}`, { headers: getAuthHeaders() });
      const ptw = res.data;
      
      // Allow high-risk forms editing in both waiting_high_risk_forms AND needs_revision status
      if (ptw.status !== 'waiting_high_risk_forms' && ptw.status !== 'needs_revision') {
        toast.error('PTW ini tidak dalam status yang benar untuk edit high risk forms');
        navigate(`/ptw/${id}`);
        return;
      }
      
      setExistingPTW(ptw);
      setCreatedPTWId(ptw.id);
      setIsFillingHighRiskForms(true);
      
      // Load high_risk_activities to show the correct forms
      setFormData(prev => ({
        ...prev,
        high_risk_activities: ptw.high_risk_activities || [],
        high_risk_forms: ptw.high_risk_forms || { 
          working_at_height: null, 
          electric_medium_voltage: null, 
          hot_work: null, 
          confined_space: null 
        }
      }));
      
if (ptw.high_risk_forms?.working_at_height?.tensi_pekerja) {
  // Jika sudah ada data tensi yang tersimpan
  const savedTensi = ptw.high_risk_forms.working_at_height.tensi_pekerja;
  const tensiWithId = savedTensi.map((item, idx) => ({
    id: idx,
    nama: item.nama,
    tekanan_darah: item.tekanan_darah || '',
    tanggal_pemeriksaan: item.tanggal_pemeriksaan || '',
    keluhan: item.keluhan || '',
    tindak_lanjut: item.tindak_lanjut || ''
  }));
  setTensiPekerja(tensiWithId);
} else if (ptw.nama_pekerja && ptw.nama_pekerja.length > 0) {
  // Jika belum ada data tensi, inisialisasi dari nama_pekerja (dengan field kosong)
  const initialTensi = ptw.nama_pekerja.map((nama, idx) => ({
    id: idx,
    nama: nama,
    tekanan_darah: '',
    tanggal_pemeriksaan: '',
    keluhan: '',
    tindak_lanjut: ''
  }));
  setTensiPekerja(initialTensi);
}

if (ptw.high_risk_forms?.confined_space?.hazard_controls_table) {
  setHazardControlsTable(ptw.high_risk_forms.confined_space.hazard_controls_table);
}

if (ptw.high_risk_forms?.confined_space) {
  setVentilasiData({
    cukup: ptw.high_risk_forms.confined_space.ventilasi_cukup || null,
    jenis: ptw.high_risk_forms.confined_space.ventilasi_jenis || null
  });
if (ptw.high_risk_forms.confined_space.permit_review) {
    setPermitReview(ptw.high_risk_forms.confined_space.permit_review);
  }
  
  if (ptw.high_risk_forms.confined_space.peralatan_table) {
    setPeralatanTable(ptw.high_risk_forms.confined_space.peralatan_table);
  }
  
  setMonitoringUdaraTerusMenerus(ptw.high_risk_forms.confined_space.monitoring_udara_terus_menerus || null);
  
  if (ptw.high_risk_forms.confined_space.pengukuran_udara_table) {
    setPengukuranUdaraTable(ptw.high_risk_forms.confined_space.pengukuran_udara_table);
  }
  
  if (ptw.high_risk_forms.confined_space.entrants_table) {
    setEntrantsTable(ptw.high_risk_forms.confined_space.entrants_table);
  }
  
  setEmergencyProcedure(ptw.high_risk_forms.confined_space.emergency_procedure || null);
  
  if (ptw.high_risk_forms.confined_space.qshe_procedure) {
    setQsheProcedure(ptw.high_risk_forms.confined_space.qshe_procedure);
  }

}



      toast.success(ptw.status === 'needs_revision' ? 'Edit form high risk detail' : 'Silakan isi form high risk detail');
    } catch (error) {
      console.error('Error fetching PTW:', error);
      toast.error('Gagal memuat data PTW');
      navigate(`/ptw/${id}`);
    }
  };

  const fetchUsers = async () => {
    try {
      const picRes = await axios.get(`${API}/users?role=pic`, { headers: getAuthHeaders() });
      setPicUsers(picRes.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Gagal memuat daftar PIC');
    }
  };

  const fetchPTWForEdit = async () => {
    try {
      const res = await axios.get(`${API}/ptw/${id}`, { headers: getAuthHeaders() });
      const ptw = res.data;
      setExistingPTW(ptw);
      setIsEditMode(true);
      
      setFormData({
        lokasi_pekerjaan: ptw.lokasi_pekerjaan || '',
        nama_pekerja: ptw.nama_pekerja || [''],
        deskripsi_pekerjaan: ptw.deskripsi_pekerjaan || '',
        mobile_phone_1: ptw.mobile_phone_1 || '',
        mobile_phone_2: ptw.mobile_phone_2 || '',
        nomor_po: ptw.nomor_po || '',
        tanggal_mulai: ptw.tanggal_mulai || '',
        high_risk_activities: ptw.high_risk_activities || [],
        jsa_table: ptw.jsa_table?.length > 0 ? ptw.jsa_table : [{ steps_of_activity: '', hazard_reaction: '', safe_plan: '', resources: '' }],
        potensi_bahaya: ptw.potensi_bahaya || [],
        risiko_lalu_lintas: ptw.risiko_lalu_lintas || {},
        apd_digunakan: ptw.apd_digunakan || [],
        personal_hygiene: ptw.personal_hygiene || {},
        isolasi_listrik: ptw.isolasi_listrik || { dilakukan: false, nama_nomor_panel: '', cara_mengisolasi: [], cara_mengisolasi_lainnya: '' },
        isolasi_utilitas: ptw.isolasi_utilitas || { dilakukan: false, jenis_utilitas: [], jenis_utilitas_lainnya: '', cara_mengisolasi: [], cara_mengisolasi_lainnya: '',
        amonia_jenis_pekerjaan: ptw.isolasi_utilitas?.amonia_jenis_pekerjaan || '',
        amonia_lokasi: ptw.isolasi_utilitas?.amonia_lokasi || '',
        amonia_nama_peralatan: ptw.isolasi_utilitas?.amonia_nama_peralatan || '',
        amonia_pemilik_area: ptw.isolasi_utilitas?.amonia_pemilik_area || '',
        amonia_loto_person: ptw.isolasi_utilitas?.amonia_loto_person || '',
        amonia_uraian_pekerjaan: ptw.isolasi_utilitas?.amonia_uraian_pekerjaan || '',
        amonia_jenis_energi: ptw.isolasi_utilitas?.amonia_jenis_energi || '',
        amonia_titik_isolasi: ptw.isolasi_utilitas?.amonia_titik_isolasi || '',
        amonia_metode_isolasi: ptw.isolasi_utilitas?.amonia_metode_isolasi || '',
        amonia_peralatan_lock: ptw.isolasi_utilitas?.amonia_peralatan_lock || '',
        amonia_apd_helmet: ptw.isolasi_utilitas?.amonia_apd_helmet || false,
        amonia_apd_sarung_tangan: ptw.isolasi_utilitas?.amonia_apd_sarung_tangan || false,
        amonia_apd_respirator: ptw.isolasi_utilitas?.amonia_apd_respirator || false,
        amonia_apd_lainnya: ptw.isolasi_utilitas?.amonia_apd_lainnya || ''
         },
        isolasi_proses: ptw.isolasi_proses || { dilakukan: false, peralatan: '', cara_mengisolasi: [], cara_mengisolasi_lainnya: '' },
        daftar_pencegahan_umum: ptw.daftar_pencegahan_umum || [],
        daftar_pencegahan_penggalian: ptw.daftar_pencegahan_penggalian || [],
        daftar_pencegahan_lainnya: ptw.daftar_pencegahan_lainnya || [],
        pic_id: ptw.pic_id || '',
        high_risk_forms: ptw.high_risk_forms || { working_at_height: null, electric_medium_voltage: null, hot_work: null, confined_space: null }
      });

      // 🔴 TAMBAHAN: Load data tensi pekerja dari existing PTW (untuk Working at Height) 🔴
if (ptw.high_risk_forms?.working_at_height?.tensi_pekerja) {
  // Jika sudah ada data tensi yang tersimpan
  const savedTensi = ptw.high_risk_forms.working_at_height.tensi_pekerja;
  const tensiWithId = savedTensi.map((item, idx) => ({
    id: idx,
    nama: item.nama,
    tekanan_darah: item.tekanan_darah || '',
    tanggal_pemeriksaan: item.tanggal_pemeriksaan || '',
    keluhan: item.keluhan || '',
    tindak_lanjut: item.tindak_lanjut || ''
  }));
  setTensiPekerja(tensiWithId);
} else if (ptw.nama_pekerja && ptw.nama_pekerja.length > 0) {
  // Jika belum ada data tensi, inisialisasi dari nama_pekerja (dengan field kosong)
  const initialTensi = ptw.nama_pekerja.map((nama, idx) => ({
    id: idx,
    nama: nama,
    tekanan_darah: '',
    tanggal_pemeriksaan: '',
    keluhan: '',
    tindak_lanjut: ''
  }));
  setTensiPekerja(initialTensi);
}

if (ptw.high_risk_forms?.confined_space?.hazard_controls_table) {
  setHazardControlsTable(ptw.high_risk_forms.confined_space.hazard_controls_table);
}

if (ptw.high_risk_forms?.confined_space) {
  setVentilasiData({
    cukup: ptw.high_risk_forms.confined_space.ventilasi_cukup || null,
    jenis: ptw.high_risk_forms.confined_space.ventilasi_jenis || null
  });
}

    } catch (error) {
      console.error('Error fetching PTW:', error);
      toast.error('Gagal memuat data PTW');
    }
  };

  const addJSARow = () => {
    setFormData({
      ...formData,
      jsa_table: [...formData.jsa_table, { steps_of_activity: '', hazard_reaction: '', safe_plan: '', resources: '' }]
    });
  };

  const removeJSARow = (index) => {
    const newTable = formData.jsa_table.filter((_, i) => i !== index);
    setFormData({ ...formData, jsa_table: newTable });
  };

  const updateJSARow = (index, field, value) => {
    const newTable = [...formData.jsa_table];
    newTable[index][field] = value;
    setFormData({ ...formData, jsa_table: newTable });
  };

  const addNamaPekerja = () => {
    setFormData({ ...formData, nama_pekerja: [...formData.nama_pekerja, ''] });
  };

  const removeNamaPekerja = (index) => {
    const newNames = formData.nama_pekerja.filter((_, i) => i !== index);
    setFormData({ ...formData, nama_pekerja: newNames });
  };

  const updateNamaPekerja = (index, value) => {
    const newNames = [...formData.nama_pekerja];
    newNames[index] = value;
    setFormData({ ...formData, nama_pekerja: newNames });
  };

  const toggleCheckbox = (field, value) => {
    const current = formData[field];
    if (current.includes(value)) {
      setFormData({ ...formData, [field]: current.filter(v => v !== value) });
    } else {
      setFormData({ ...formData, [field]: [...current, value] });
    }
  };

  const toggleDictCheckbox = (field, key) => {
    const current = formData[field];
    setFormData({
      ...formData,
      [field]: { ...current, [key]: !current[key] }
    });
  };

const updateTensiPekerja = (index, field, value) => {
  // 1. Update state tensiPekerja
  const updated = [...tensiPekerja];
  
  if (updated[index]) {
    updated[index] = { ...updated[index], [field]: value };
  } else {
    updated[index] = {
      id: index,
      nama: formData.nama_pekerja[index] || '',
      tekanan_darah: '',
      tanggal_pemeriksaan: '',
      keluhan: '',
      tindak_lanjut: '',
      [field]: value
    };
  }
  
  setTensiPekerja(updated);
  
  // 2. Simpan juga ke formData.high_risk_forms.working_at_height.tensi_pekerja
  setFormData(prev => ({
    ...prev,
    high_risk_forms: {
      ...prev.high_risk_forms,
      working_at_height: {
        ...prev.high_risk_forms.working_at_height,
        tensi_pekerja: updated.map(p => ({
          nama: p.nama,
          tekanan_darah: p.tekanan_darah || '',
          tanggal_pemeriksaan: p.tanggal_pemeriksaan || '',
          keluhan: p.keluhan || '',
          tindak_lanjut: p.tindak_lanjut || ''
        }))
      }
    }
  }));
};

const updateHazardControlsTable = (rowId, field, value) => {
  const updated = hazardControlsTable.map(row => {
    if (row.id === rowId) {
      // Jika kolom "Tidak" di-check, disable kolom 4,5,6 dan kosongkan
      if (field === 'tidak' && value === true) {
        return { 
          ...row, 
          tidak: true, 
          ya: false,
          isolasi_sebelum: false,
          isolasi_setelah: false,
          efek_ke_lokasi_lain: ''
        };
      }
      // Jika kolom "Ya" di-check, enable kembali kolom 4,5,6
      if (field === 'ya' && value === true) {
        return { ...row, ya: true, tidak: false };
      }
      // Untuk field lainnya (isolasi_sebelum, isolasi_setelah, efek_ke_lokasi_lain)
      return { ...row, [field]: value };
    }
    return row;
  });
  
  // Update state
  setHazardControlsTable(updated);
  
  // Simpan ke formData agar ikut terkirim ke backend
  setFormData(prev => ({
    ...prev,
    high_risk_forms: {
      ...prev.high_risk_forms,
      confined_space: {
        ...prev.high_risk_forms.confined_space,
        hazard_controls_table: updated
      }
    }
  }));
};  // 🔴 INI KURUNG TUTUP UNTUK updateHazardControlsTable

// 🔴 FUNGSI UPDATE VENTILASI (HARUS BERDIRI SENDIRI)
const updateVentilasi = (field, value) => {
  setVentilasiData(prev => {
    const updated = { ...prev, [field]: value };
    
    // Jika memilih "Ya" pada ventilasi_cukup, reset jenis ventilasi
    if (field === 'cukup' && value === 'Ya') {
      updated.jenis = null;
    }
    
    return updated;
  });
  
  // Simpan ke formData
  setFormData(prev => ({
    ...prev,
    high_risk_forms: {
      ...prev.high_risk_forms,
      confined_space: {
        ...prev.high_risk_forms.confined_space,
        ventilasi_cukup: field === 'cukup' ? value : prev.high_risk_forms.confined_space?.ventilasi_cukup,
        ventilasi_jenis: field === 'jenis' ? value : prev.high_risk_forms.confined_space?.ventilasi_jenis
      }
    }
  }));
};

// 🔴 STEP 5: Fungsi untuk Tabel Peralatan (Section 13)
const addPeralatanRow = () => {
  const newId = Math.max(...peralatanTable.map(p => p.id), 0) + 1;
  setPeralatanTable([...peralatanTable, { id: newId, model: '', serial_number: '', tanggal_kalibrasi: '', pemilik: '' }]);
};

const updatePeralatanRow = (id, field, value) => {
  const updated = peralatanTable.map(row => 
    row.id === id ? { ...row, [field]: value } : row
  );
  setPeralatanTable(updated);
  
  // Simpan ke formData
  setFormData(prev => ({
    ...prev,
    high_risk_forms: {
      ...prev.high_risk_forms,
      confined_space: {
        ...prev.high_risk_forms.confined_space,
        peralatan_table: updated
      }
    }
  }));
};

const deletePeralatanRow = (id) => {
  if (peralatanTable.length === 1) return;
  setPeralatanTable(peralatanTable.filter(row => row.id !== id));
};

// 🔴 STEP 5: Fungsi untuk Tabel Pengukuran Udara (Section 13)
const addPengukuranUdaraRow = () => {
  const newId = Math.max(...pengukuranUdaraTable.map(p => p.id), 0) + 1;
  setPengukuranUdaraTable([...pengukuranUdaraTable, { 
    id: newId, tanggal: '', waktu: '', isolasi: '', oksigen: '', combustible: '', lel: '', h2s: '', co: '', toxic: '' 
  }]);
};

const updatePengukuranUdaraRow = (id, field, value) => {
  const updated = pengukuranUdaraTable.map(row => 
    row.id === id ? { ...row, [field]: value } : row
  );
  setPengukuranUdaraTable(updated);
  
  setFormData(prev => ({
    ...prev,
    high_risk_forms: {
      ...prev.high_risk_forms,
      confined_space: {
        ...prev.high_risk_forms.confined_space,
        pengukuran_udara_table: updated
      }
    }
  }));
};

const deletePengukuranUdaraRow = (id) => {
  if (pengukuranUdaraTable.length === 1) return;
  setPengukuranUdaraTable(pengukuranUdaraTable.filter(row => row.id !== id));
};

// 🔴 STEP 5: Fungsi untuk Tabel Entrants (Section 14)
const addEntrantsRow = () => {
  const newId = Math.max(...entrantsTable.map(e => e.id), 0) + 1;
  setEntrantsTable([...entrantsTable, { id: newId, nama_entrants: '', nama_standby: '' }]);
};

const updateEntrantsRow = (id, field, value) => {
  const updated = entrantsTable.map(row => 
    row.id === id ? { ...row, [field]: value } : row
  );
  setEntrantsTable(updated);
  
  setFormData(prev => ({
    ...prev,
    high_risk_forms: {
      ...prev.high_risk_forms,
      confined_space: {
        ...prev.high_risk_forms.confined_space,
        entrants_table: updated
      }
    }
  }));
};

const deleteEntrantsRow = (id) => {
  if (entrantsTable.length === 1) return;
  setEntrantsTable(entrantsTable.filter(row => row.id !== id));
};

// 🔴 STEP 5: Fungsi untuk update Permit Review (radio buttons)
const updatePermitReview = (field, value) => {
  const updated = { ...permitReview, [field]: value };
  setPermitReview(updated);
  
  setFormData(prev => ({
    ...prev,
    high_risk_forms: {
      ...prev.high_risk_forms,
      confined_space: {
        ...prev.high_risk_forms.confined_space,
        permit_review: updated
      }
    }
  }));
};

// 🔴 STEP 5: Fungsi untuk update QSHE Procedure
const updateQsheGuidelines = (value) => {
  const updated = qsheProcedure.guidelines.includes(value)
    ? qsheProcedure.guidelines.filter(g => g !== value)
    : [...qsheProcedure.guidelines, value];
  
  setQsheProcedure({ ...qsheProcedure, guidelines: updated });
  
  setFormData(prev => ({
    ...prev,
    high_risk_forms: {
      ...prev.high_risk_forms,
      confined_space: {
        ...prev.high_risk_forms.confined_space,
        qshe_procedure: { ...qsheProcedure, guidelines: updated }
      }
    }
  }));
};

const updateQsheNamaProcedure = (value) => {
  setQsheProcedure({ ...qsheProcedure, nama_procedure: value });
  
  setFormData(prev => ({
    ...prev,
    high_risk_forms: {
      ...prev.high_risk_forms,
      confined_space: {
        ...prev.high_risk_forms.confined_space,
        qshe_procedure: { ...qsheProcedure, nama_procedure: value }
      }
    }
  }));
};

const updateQsheRiskAssessment = (value) => {
  setQsheProcedure({ ...qsheProcedure, risk_assessment: value });
  
  setFormData(prev => ({
    ...prev,
    high_risk_forms: {
      ...prev.high_risk_forms,
      confined_space: {
        ...prev.high_risk_forms.confined_space,
        qshe_procedure: { ...qsheProcedure, risk_assessment: value }
      }
    }
  }));
};

  // ==================== VALIDASI STEP YANG DIUPDATE ====================
  
  const validateStep = (step) => {
    let errors = {};
    
    // Mode Closing
    // Mode Closing - Hanya disclaimer yang wajib, checklist opsional
if (isClosingMode) {
  let errors = {};
  if (!closingData.closing_disclaimer_accepted) {
    errors.closing_disclaimer = 'Anda harus menyetujui disclaimer';
  }
  setValidationErrors(errors);
  return Object.keys(errors).length === 0;
}
    
    // Mode High Risk Forms
        // Mode High Risk Forms
        // Mode High Risk Forms
    if (isFillingHighRiskForms) {
      const extraStates = {
        hazardControlsTable,
        ventilasiData,
        permitReview,
        monitoringUdaraTerusMenerus,
        emergencyProcedure,
        qsheProcedure
      };
      errors = validateAllHighRiskForms(formData, extraStates);
      setValidationErrors(errors);
      
      // 🔴 SCROLL KE ERROR PALING ATAS jika ada error
      if (Object.keys(errors).length > 0) {
        setTimeout(() => {
  // Cari elemen error
  const errorElements = document.querySelectorAll(
    'input.border-red-500, select.border-red-500, textarea.border-red-500, .error-radio-group'
  );
  
  console.log('Jumlah elemen error ditemukan:', errorElements.length);
  
  if (errorElements.length > 0) {
    let topmostError = errorElements[0];
    let minTop = topmostError.getBoundingClientRect().top;
    
    for (let i = 1; i < errorElements.length; i++) {
      const top = errorElements[i].getBoundingClientRect().top;
      if (top < minTop) {
        minTop = top;
        topmostError = errorElements[i];
      }
    }
    
    console.log('Scroll ke elemen:', topmostError);
    
    // Gunakan scrollIntoView dengan opsi lengkap
    topmostError.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'center',
      inline: 'nearest'
    });
    
    // Fokus ke field
    if (topmostError.focus) {
      topmostError.focus();
    }
    
    // Highlight
    topmostError.style.transition = 'background-color 0.5s';
    topmostError.style.backgroundColor = '#fee2e2';
    setTimeout(() => {
      topmostError.style.backgroundColor = '';
    }, 1000);
  } else {
    console.log('TIDAK ADA elemen error dengan selector yang ditentukan');
    console.log('Coba cari semua .border-red-500:', document.querySelectorAll('.border-red-500').length);
  }
}, 200); // Delay ditambah dari 100ms jadi 200ms
      }
      
      return Object.keys(errors).length === 0;
    }
    
    // Mode PTW Utama - validasi berdasarkan step saat ini
    if (step === 0) {
      // Validasi STEP 1: Info Umum
      if (!formData.lokasi_pekerjaan?.trim()) {
        errors.lokasi_pekerjaan = 'Lokasi pekerjaan wajib diisi';
      }
      if (!formData.deskripsi_pekerjaan?.trim()) {
        errors.deskripsi_pekerjaan = 'Deskripsi pekerjaan wajib diisi';
      }
      if (!formData.tanggal_mulai) {
        errors.tanggal_mulai = 'Tanggal mulai wajib dipilih';
      }
      if (!formData.mobile_phone_1?.trim()) {
        errors.mobile_phone_1 = 'Nomor HP 1 wajib diisi';
      }
      if (!formData.mobile_phone_2?.trim()) {
        errors.mobile_phone_2 = 'Nomor HP 2 wajib diisi';
      }
      const validNamaPekerja = formData.nama_pekerja?.filter(n => n?.trim()).length > 0;
      if (!validNamaPekerja) {
        errors.nama_pekerja = 'Minimal 1 nama pekerja wajib diisi';
      }
    }
    
    if (step === 1) {
      // Validasi STEP 2: JSA
      const hasValidJSA = formData.jsa_table.some(row => row.steps_of_activity && row.steps_of_activity.trim());
      if (!hasValidJSA) {
        errors.jsa_table = 'JSA wajib diisi minimal 1 baris dengan langkah aktivitas';
      }
    }
    
    if (step === 5) {
      // Validasi STEP Review (step index 5)
      if (!formData.pic_id && !isEditMode) {
        errors.pic_id = 'PIC wajib dipilih';
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  // Helper untuk styling input berdasarkan error
  const getInputClass = (fieldName) => {
    return `w-full p-3 border rounded-lg ${validationErrors[fieldName] ? 'border-red-500 bg-red-50' : 'border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:border-transparent`;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    } else {
      toast.error('Mohon lengkapi field yang required');
    }
  };

  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) {
      toast.error('Mohon lengkapi field yang required');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        nama_pekerja: formData.nama_pekerja.filter(n => n.trim()),
        jsa_table: formData.jsa_table.filter(row => 
          row.steps_of_activity || row.hazard_reaction || row.safe_plan || row.resources
        ),

 isolasi_utilitas: {
        dilakukan: formData.isolasi_utilitas.dilakukan,
        jenis_utilitas: formData.isolasi_utilitas.jenis_utilitas,
        jenis_utilitas_lainnya: formData.isolasi_utilitas.jenis_utilitas_lainnya,
        cara_mengisolasi: formData.isolasi_utilitas.cara_mengisolasi,
        cara_mengisolasi_lainnya: formData.isolasi_utilitas.cara_mengisolasi_lainnya,
        // 🔴 TAMBAHKAN 14 FIELD AMONIA DI SINI 🔴
        amonia_jenis_pekerjaan: formData.isolasi_utilitas.amonia_jenis_pekerjaan || '',
        amonia_lokasi: formData.isolasi_utilitas.amonia_lokasi || '',
        amonia_nama_peralatan: formData.isolasi_utilitas.amonia_nama_peralatan || '',
        amonia_pemilik_area: formData.isolasi_utilitas.amonia_pemilik_area || '',
        amonia_loto_person: formData.isolasi_utilitas.amonia_loto_person || '',
        amonia_uraian_pekerjaan: formData.isolasi_utilitas.amonia_uraian_pekerjaan || '',
        amonia_jenis_energi: formData.isolasi_utilitas.amonia_jenis_energi || '',
        amonia_titik_isolasi: formData.isolasi_utilitas.amonia_titik_isolasi || '',
        amonia_metode_isolasi: formData.isolasi_utilitas.amonia_metode_isolasi || '',
        amonia_peralatan_lock: formData.isolasi_utilitas.amonia_peralatan_lock || '',
        amonia_apd_helmet: formData.isolasi_utilitas.amonia_apd_helmet || false,
        amonia_apd_sarung_tangan: formData.isolasi_utilitas.amonia_apd_sarung_tangan || false,
        amonia_apd_respirator: formData.isolasi_utilitas.amonia_apd_respirator || false,
        amonia_apd_lainnya: formData.isolasi_utilitas.amonia_apd_lainnya || ''
      }

      };
      console.log('PAYLOAD YANG DIKIRIM:', JSON.stringify(payload.isolasi_utilitas, null, 2));

      if (isEditMode) {
        await axios.put(`${API}/ptw/${id}`, payload, { headers: getAuthHeaders() });
        toast.success('PTW berhasil diupdate');
        navigate(`/ptw/${id}`);
      } else {
        // Option B Flow: Create PTW first
        const res = await axios.post(`${API}/ptw`, payload, { headers: getAuthHeaders() });
        const newPTW = res.data;
        
        if (newPTW.status === 'waiting_high_risk_forms') {
          // Stay in form, switch to high-risk filling mode
          setCreatedPTWId(newPTW.id);
          setIsFillingHighRiskForms(true);
          setCurrentStep(0);
          toast.success('PTW dibuat! Silakan isi form high risk detail.');
        } else {
          toast.success('PTW berhasil dibuat dan dikirim ke PIC');
          navigate('/my-requests');
        }
      }
    } catch (error) {
      console.error('Error submitting PTW:', error);
      toast.error(error.response?.data?.detail || 'Gagal menyimpan PTW');
    } finally {
      setLoading(false);
    }
  };

      const handleSubmitHighRiskForms = async () => {
    // 🔴 CEK: Apakah semua high risk forms sudah diinisialisasi?
    const highRiskActivities = formData.high_risk_activities || [];
    const missingForms = [];
    
    if (highRiskActivities.includes('Working at Height') && !formData.high_risk_forms.working_at_height) {
      missingForms.push('Working at Height');
    }
    if (highRiskActivities.includes('Hot Work') && !formData.high_risk_forms.hot_work) {
      missingForms.push('Hot Work');
    }
    if (highRiskActivities.includes('Electric Medium Voltage') && !formData.high_risk_forms.electric_medium_voltage) {
      missingForms.push('Electric Medium Voltage');
    }
    if (highRiskActivities.includes('Confined Space') && !formData.high_risk_forms.confined_space) {
      missingForms.push('Confined Space');
    }
    
    if (missingForms.length > 0) {
      toast.error(`Silakan klik "Mulai Isi Form" terlebih dahulu untuk: ${missingForms.join(', ')}`);
      return;
    }
    
    // Validasi field required
    if (!validateStep(currentStep)) {
    return;
}

    const ptwId = createdPTWId || id;
    if (!ptwId) {
      toast.error('ID PTW tidak ditemukan');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/ptw/${ptwId}/high-risk-forms`,
        { high_risk_forms: formData.high_risk_forms },
        { headers: getAuthHeaders() }
      );
      
      toast.success('High-risk forms berhasil disubmit!');
      navigate(`/ptw/${ptwId}`);
    } catch (error) {
      console.error('Error submitting high-risk forms:', error);
      toast.error(error.response?.data?.detail || 'Gagal submit high-risk forms');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitClosing = async () => {
    if (!validateStep(currentStep)) {
      toast.error('Mohon centang disclaimer terlebih dahulu');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/ptw/${id}/close`,
        closingData,
        { headers: getAuthHeaders() }
      );
      
      toast.success('Closing berhasil disubmit! Menunggu approval PIC.');
      navigate(`/ptw/${id}`);
    } catch (error) {
      console.error('Error submitting closing:', error);
      toast.error(error.response?.data?.detail || 'Gagal submit closing');
    } finally {
      setLoading(false);
    }
  };

  const initializeHighRiskForm = (formType) => {
    const forms = { ...formData.high_risk_forms };
    
    if (formType === 'working_at_height' && !forms.working_at_height) {

      // 🔴 TAMBAHAN: Inisialisasi data tensi pekerja dari formData.nama_pekerja 🔴
  const initialTensiPekerja = formData.nama_pekerja && formData.nama_pekerja.length > 0
    ? formData.nama_pekerja.map((nama, idx) => ({
        id: idx,
        nama: nama,
        tekanan_darah: '',
        tanggal_pemeriksaan: '',
        keluhan: '',
        tindak_lanjut: ''
      }))
    : [];
  
  // Set state tensiPekerja
  setTensiPekerja(initialTensiPekerja);

      forms.working_at_height = {
        workers_trained_disclaimer: false,

        tensi_pekerja: initialTensiPekerja.map(p => ({
      nama: p.nama,
      tekanan_darah: p.tekanan_darah,
      tanggal_pemeriksaan: p.tanggal_pemeriksaan,
      keluhan: p.keluhan,
      tindak_lanjut: p.tindak_lanjut
    })),

        power_supply_area_clear: '', power_supply_notes: '',
        ladder_condition_good: '', area_safe_movement: '', aluminum_ladder_electric_area: '',
        equipment_belt_attached: '', ladder_pedestrian_positioned: '', ladder_lock_secured: '',
        ladder_extension_proper: '', ladder_notes: '',
        ppe_safety_belt_harness: false, ppe_goggles: false, ppe_gloves: false,
        ppe_safety_boots: false, ppe_protective_clothing: false, ppe_safety_helmet: false, ppe_other: '',
        forklift_platform_licensed: '', forklift_area_clear_electric: '', forklift_fall_harness_used: '',
        forklift_mobile_platform_osha: '', scaffolding_osha_compliant: '', scaffolding_toe_guard_rails: '',
        mobile_scaffolding_locked: '', outrigger_installed: '', work_area_clear_electric: '', forklift_notes: '',
        precaution_barricade: false, precaution_special_warning: '',
        guidelines_acknowledged: false
      };
    } else if (formType === 'hot_work' && !forms.hot_work) {
      forms.hot_work = {
        work_type_welding_brazing: false, work_type_cutting: false, work_type_grinding: false,
        work_type_soldering: false, work_type_thawing: false,
        checklist_sprinkler_hydrant: '', checklist_equipment_condition: '', checklist_fire_extinguisher: '',
        area_12m_material_cleared: '', area_12m_no_explosive: '', area_12m_floor_protected: '',
        area_12m_walls_openings_protected: '', area_12m_passage_closed: '',
        wall_ceiling_combustible: '', wall_ceiling_isolated: '',
        confined_space_cleaned: '', confined_space_container_emptied: '', confined_space_permit_used: '',
        monitoring_fire_watch_2hrs: '', monitoring_she_has_extinguisher: '',
        monitoring_she_trained: '', monitoring_she_required_special: '',
        other_aspects: '', warning_sign_disclaimer: false
      };
    } else if (formType === 'electric_medium_voltage' && !forms.electric_medium_voltage) {
      forms.electric_medium_voltage = {
        workers_briefing_disclaimer: false,
        hazard_live_mv_busbar: false, hazard_live_incoming_cable: false, hazard_charged_transformer: false,
        hazard_rain_outdoor: false, hazard_wet_floor: false, hazard_falling_objects: false,
        hazard_explosion: false, hazard_confined_area: false,
        checklist_outgoing_switch_off: '', checklist_outgoing_grounded: '', checklist_outgoing_ground_point: '',
        checklist_incoming_switch_off: '', checklist_incoming_busbar_grounded: '', checklist_incoming_ground_point: '',
        checklist_voltmeter_off: '', checklist_incoming_breaker_loto: '', checklist_outgoing_breaker_loto: '',
        checklist_manual_clamp_grounding: '', checklist_incoming_cable_off: '', checklist_transformer_ground_ok: '',
        isolation_uniform_safety_shoes: '', isolation_electric_gloves: '', isolation_safety_glasses: '',
        isolation_tools_certified: '', isolation_work_at_height_permit: '', isolation_fire_extinguisher_abc: ''
      };
    } else if (formType === 'confined_space' && !forms.confined_space) {
      forms.confined_space = {
        type_storage_vessels: false, type_underground_space: false, type_shortcut_ladder: false,
        type_silo_drum_mixer: false, type_electrical_room: false, type_under_roof_space: false,
        type_bins_special_room: false, type_other: '',
        work_inspection: false, work_inside_confined: false,
        materials_chemicals: '',
        // Risk Assessment removed per user request
        hazard_atm_flammable: false, hazard_atm_toxic: false, hazard_atm_irritant: false,
        hazard_atm_corrosive: false, hazard_atm_oxygen_deficient: false, hazard_atm_oxygen_enriched: false,
        hazard_atm_other: '',
        hazard_phy_temperature: false, hazard_phy_chemical_absorption: false, hazard_phy_noise: false,
        hazard_phy_entrapment: false, hazard_phy_vibration: false, hazard_phy_electrical_equipment: false,
        hazard_phy_mechanical_equipment: false, hazard_phy_spark_operations: false, hazard_phy_spilled_liquids: false,
        hazard_phy_engulfment: false, hazard_phy_radiation: false, hazard_phy_entry_exit_limitation: false,
        hazard_phy_other: '',
        ppe_resp_scba: false, ppe_resp_air_purifying: false, ppe_resp_air_line: false,
        ppe_eye_safety_glasses: false, ppe_eye_goggles: false,
        ppe_routine_gloves: false, ppe_routine_chemical_suit: false, ppe_routine_hard_hat: false,
        ppe_routine_overall: false, ppe_routine_earplug: false, ppe_routine_safety_boots: false,
        ppe_rescue_escape_set: false, ppe_rescue_communication: false, ppe_rescue_motion_detector: false,
        ppe_rescue_life_line: false, ppe_rescue_lifting_gear: false,
        ppe_essential_fire_extinguisher: false, ppe_essential_first_aid: false, ppe_essential_torch: false,
        ppe_other: '',
        hot_work_area_clear_15m: '', hot_work_fire_extinguisher_available: '', hot_work_extinguisher_type: '',
        hot_work_air_free_gas: '', hot_work_entry_exit_safe: '', hot_work_welding_exists: '',
        hot_work_cert_asme: '', hot_work_cert_other: '', hot_work_equipment_list: '',
        hot_work_type_acetylene: false, hot_work_type_arc_welding: false, hot_work_type_oxygen_cutting: false,
        hot_work_cylinder_certified: '', hot_work_cylinder_test_available: '',
        hot_work_hose_condition: '', hot_work_hose_notes: '',
        hot_work_coupling_condition: '', hot_work_coupling_notes: '',
        equipment_declaration: '',
        control_loto_required: '', control_loto_points: '', control_gfci_required: '',
        control_explosion_proof_required: '', control_barrier_required: '', control_communication_required: '',
        control_emergency_phone_radio: '', control_equipment_fire_extinguisher: '',
        control_equipment_emergency_shower: '', control_equipment_safety_harness: '',
        control_equipment_life_line: '', control_equipment_tripod_winch: '', control_equipment_other: '',
        control_msds_attached: '', control_workers_trained: '', control_standby_notified: '',
        control_space_cleaned: '', control_cleaning_method: '', control_cleaning_involves_gas: '',
        control_gas_type: '', control_space_emptied: '',
        hazard_controls_table: [],
        ventilasi_cukup: null,
        ventilasi_jenis: null,
        permit_review: {
          monitoring_udara: null,
          respirator_diperlukan: null,
          hazard_controls_ppe: null,
          plant_safety_approval: null,
          monitoring_berkelanjutan: null
        },
        peralatan_table: [],
        monitoring_udara_terus_menerus: null,
        pengukuran_udara_table: [],
        entrants_table: [],
        emergency_procedure: null,
        qshe_procedure: {
          guidelines: [],
          nama_procedure: '',
          risk_assessment: null
        },
        declaration_understand_work: false
      };
      const initialHazardTable = [
    { id: 1, pertanyaan: 'Water/Gas/Steam/Chemical', ya: false, tidak: false, isolasi_sebelum: false, isolasi_setelah: false, efek_ke_lokasi_lain: '' },
    { id: 2, pertanyaan: 'Mechanical/Electrical Drives', ya: false, tidak: false, isolasi_sebelum: false, isolasi_setelah: false, efek_ke_lokasi_lain: '' },
    { id: 3, pertanyaan: 'Auto/Fire extinguisher system', ya: false, tidak: false, isolasi_sebelum: false, isolasi_setelah: false, efek_ke_lokasi_lain: '' },
    { id: 4, pertanyaan: 'Hydraulic/Electric/Gas Power', ya: false, tidak: false, isolasi_sebelum: false, isolasi_setelah: false, efek_ke_lokasi_lain: '' },
    { id: 5, pertanyaan: 'Other (Specify)', ya: false, tidak: false, isolasi_sebelum: false, isolasi_setelah: false, efek_ke_lokasi_lain: '' },
    { id: 6, pertanyaan: 'Tag fixed to isolation point', ya: false, tidak: false, isolasi_sebelum: false, isolasi_setelah: false, efek_ke_lokasi_lain: '' }
  ];
  setHazardControlsTable(initialHazardTable);
}
      
    
    
    setFormData({ ...formData, high_risk_forms: forms });
  };

  const renderClosingForm = () => {
    return (
      <div className="space-y-6">
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <h2 className="text-xl font-semibold text-purple-800 mb-2">PEKERJAAN SELESAI</h2>
          <p className="text-sm text-purple-700">
            Saya selaku pelaksana pekerjaan menyatakan bahwa pekerjaan sesuai ijin kerja ini telah selesai 
            dan telah memastikan hal berikut ini telah dilakukan:
          </p>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-800 mb-4">Checklist Closing</h3>
          <div className="space-y-3">
            {closingChecklistItems.map(item => (
              <label key={item.key} className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={closingData.closing_checklist[item.key] || false}
                  onChange={() => {
                    setClosingData(prev => ({
                      ...prev,
                      closing_checklist: {
                        ...prev.closing_checklist,
                        [item.key]: !prev.closing_checklist[item.key]
                      }
                    }));
                  }}
                  className="mt-1 w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <span className="text-sm text-gray-700 group-hover:text-gray-900">{item.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-800 mb-4">Catatan Tambahan</h3>
          <textarea
            value={closingData.closing_notes}
            onChange={(e) => setClosingData(prev => ({ ...prev, closing_notes: e.target.value }))}
            className="w-full p-3 border border-gray-300 rounded-lg"
            rows="3"
            placeholder="Catatan tambahan (opsional)"
          />
        </div>

        <div className={`bg-yellow-50 border rounded-lg p-4 ${validationErrors.closing_disclaimer ? 'border-red-500' : 'border-yellow-200'}`}>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={closingData.closing_disclaimer_accepted}
              onChange={() => {
                setClosingData(prev => ({
                  ...prev,
                  closing_disclaimer_accepted: !prev.closing_disclaimer_accepted
                }));
                if (validationErrors.closing_disclaimer) {
                  setValidationErrors(prev => ({ ...prev, closing_disclaimer: null }));
                }
              }}
              className="mt-1 w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            />
            <span className="text-sm text-yellow-800 font-medium">
              Saya menyatakan bahwa semua sudah sesuai dengan pedoman safety, dan pekerjaan sudah selesai. *
            </span>
          </label>
          {validationErrors.closing_disclaimer && (
            <p className="text-red-500 text-xs mt-2">{validationErrors.closing_disclaimer}</p>
          )}
        </div>
      </div>
    );
  };

  const renderStepContent = () => {
    // Closing mode - render closing form
    if (isClosingMode) {
      return renderClosingForm();
    }
    
    if (isFillingHighRiskForms) {
      return renderHighRiskFormsContent();
    }

    const stepName = steps[currentStep];

    // Step 0: Info Umum
    if (stepName === 'Info Umum') {
      return (
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-gray-800">Informasi Umum</h2>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Lokasi Pekerjaan <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.lokasi_pekerjaan}
              onChange={(e) => setFormData({ ...formData, lokasi_pekerjaan: e.target.value })}
              className={getInputClass('lokasi_pekerjaan')}
              placeholder="e.g., Area Produksi A"
            />
            {validationErrors.lokasi_pekerjaan && (
              <p className="text-red-500 text-xs mt-1">{validationErrors.lokasi_pekerjaan}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Deskripsi Pekerjaan <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.deskripsi_pekerjaan}
              onChange={(e) => setFormData({ ...formData, deskripsi_pekerjaan: e.target.value })}
              className={getInputClass('deskripsi_pekerjaan')}
              rows="3"
              placeholder="Jelaskan pekerjaan yang akan dilakukan"
            />
            {validationErrors.deskripsi_pekerjaan && (
              <p className="text-red-500 text-xs mt-1">{validationErrors.deskripsi_pekerjaan}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nama Pekerja <span className="text-red-500">*</span>
            </label>
            {formData.nama_pekerja.map((nama, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={nama}
                  onChange={(e) => updateNamaPekerja(index, e.target.value)}
                  className="flex-1 p-3 border border-gray-300 rounded-lg"
                  placeholder={`Nama pekerja ${index + 1}`}
                />
                {formData.nama_pekerja.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeNamaPekerja(index)}
                    className="p-3 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 size={20} />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addNamaPekerja}
              className="mt-2 flex items-center gap-2 text-blue-600 hover:text-blue-700"
            >
              <Plus size={16} /> Tambah Pekerja
            </button>
            {validationErrors.nama_pekerja && (
              <p className="text-red-500 text-xs mt-1">{validationErrors.nama_pekerja}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nomor HP 1 <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                value={formData.mobile_phone_1}
                onChange={(e) => setFormData({ ...formData, mobile_phone_1: e.target.value })}
                className={getInputClass('mobile_phone_1')}
                placeholder="08xxxxxxxxxx"
              />
              {validationErrors.mobile_phone_1 && (
                <p className="text-red-500 text-xs mt-1">{validationErrors.mobile_phone_1}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nomor HP 2 <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                value={formData.mobile_phone_2}
                onChange={(e) => setFormData({ ...formData, mobile_phone_2: e.target.value })}
                className={getInputClass('mobile_phone_2')}
                placeholder="08xxxxxxxxxx"
              />
              {validationErrors.mobile_phone_2 && (
                <p className="text-red-500 text-xs mt-1">{validationErrors.mobile_phone_2}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nomor PO (Optional)</label>
              <input
                type="text"
                value={formData.nomor_po}
                onChange={(e) => setFormData({ ...formData, nomor_po: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg"
                placeholder="92011111"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tanggal Pengerjaan <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.tanggal_mulai}
                onChange={(e) => setFormData({ ...formData, tanggal_mulai: e.target.value })}
                className={getInputClass('tanggal_mulai')}
              />
              {validationErrors.tanggal_mulai && (
                <p className="text-red-500 text-xs mt-1">{validationErrors.tanggal_mulai}</p>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Step 1: JSA & High Risk
    if (stepName === 'JSA & High Risk') {
      return (
        <div className="space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Job Safety Analysis (JSA)</h2>
            {validationErrors.jsa_table && (
              <p className="text-red-500 text-sm mb-2">{validationErrors.jsa_table}</p>
            )}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border p-2 text-left">Langkah Aktifitas</th>
                    <th className="border p-2 text-left">Bahaya</th>
                    <th className="border p-2 text-left">Risiko</th>
                    <th className="border p-2 text-left">Pengendalian Risiko</th>
                    <th className="border p-2 w-20">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.jsa_table.map((row, index) => (
                    <tr key={index}>
                      <td className="border p-2">
                        <input
                          type="text"
                          value={row.steps_of_activity}
                          onChange={(e) => updateJSARow(index, 'steps_of_activity', e.target.value)}
                          className="w-full p-2 border-0"
                          placeholder="Harap Isi"
                        />
                       </td>
                      <td className="border p-2">
                        <input
                          type="text"
                          value={row.hazard_reaction}
                          onChange={(e) => updateJSARow(index, 'hazard_reaction', e.target.value)}
                          className="w-full p-2 border-0"
                          placeholder="Harap Isi"
                        />
                       </td>
                      <td className="border p-2">
                        <input
                          type="text"
                          value={row.safe_plan}
                          onChange={(e) => updateJSARow(index, 'safe_plan', e.target.value)}
                          className="w-full p-2 border-0"
                          placeholder="Harap Isi"
                        />
                       </td>
                      <td className="border p-2">
                        <input
                          type="text"
                          value={row.resources}
                          onChange={(e) => updateJSARow(index, 'resources', e.target.value)}
                          className="w-full p-2 border-0"
                          placeholder="Harap Isi"
                        />
                       </td>
                      <td className="border p-2 text-center">
                        {formData.jsa_table.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeJSARow(index)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 size={18} />
                          </button>
                        )}
                       </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button
                type="button"
                onClick={addJSARow}
                className="mt-2 flex items-center gap-2 text-blue-600 hover:text-blue-700"
              >
                <Plus size={16} /> Tambah Baris JSA
              </button>
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">High Risk Activities</h2>
            {formData.high_risk_activities.length > 0 && (
              <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  ⚠️ Anda telah memilih high risk activities. Setelah submit form utama, Anda akan diminta mengisi form detail high risk.
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              {highRiskOptions.map((option) => (
                <label key={option} className="flex items-center gap-2 p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={formData.high_risk_activities.includes(option)}
                    onChange={() => toggleCheckbox('high_risk_activities', option)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">{option}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      );
    }

    // Step: Bahaya & APD
    if (stepName === 'Bahaya & APD') {
      return (
        <div className="space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Potensi Bahaya</h2>
            <div className="grid grid-cols-2 gap-2">
              {potensiBahayaOptions.map((option) => (
                <label key={option} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={formData.potensi_bahaya.includes(option)}
                    onChange={() => toggleCheckbox('potensi_bahaya', option)}
                    className="w-4 h-4"
                  />
                  {option}
                </label>
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Resiko Lalu Lintas</h2>
            <div className="grid grid-cols-2 gap-2">
              {risikoLaluLintasOptions.map((option) => (
                <label key={option} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={formData.risiko_lalu_lintas[option] || false}
                    onChange={() => toggleDictCheckbox('risiko_lalu_lintas', option)}
                    className="w-4 h-4"
                  />
                  {option}
                </label>
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">APD yang Digunakan</h2>
            <div className="grid grid-cols-2 gap-2">
              {apdOptions.map((option) => (
                <label key={option} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={formData.apd_digunakan.includes(option)}
                    onChange={() => toggleCheckbox('apd_digunakan', option)}
                    className="w-4 h-4"
                  />
                  {option}
                </label>
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Personal Hygiene</h2>
            <div className="grid grid-cols-2 gap-2">
              {personalHygieneOptions.map((option) => (
                <label key={option} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={formData.personal_hygiene[option] || false}
                    onChange={() => toggleDictCheckbox('personal_hygiene', option)}
                    className="w-4 h-4"
                  />
                  {option}
                </label>
              ))}
            </div>
          </div>
        </div>
      );
    }

    // Step: Isolasi
    if (stepName === 'Isolasi') {
      return (
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Isolasi</h2>

          {/* Isolasi Listrik */}
          <div className="border border-gray-300 rounded-lg p-4">
            <label className="flex items-center gap-2 mb-4">
              <input
                type="checkbox"
                checked={formData.isolasi_listrik.dilakukan}
                onChange={(e) => setFormData({
                  ...formData,
                  isolasi_listrik: { ...formData.isolasi_listrik, dilakukan: e.target.checked }
                })}
                className="w-4 h-4"
              />
              <span className="font-semibold">Isolasi Listrik</span>
            </label>

            {formData.isolasi_listrik.dilakukan && (
              <div className="space-y-4 ml-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nama / Nomor Panel</label>
                  <input
                    type="text"
                    value={formData.isolasi_listrik.nama_nomor_panel}
                    onChange={(e) => setFormData({
                      ...formData,
                      isolasi_listrik: { ...formData.isolasi_listrik, nama_nomor_panel: e.target.value }
                    })}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    placeholder="e.g., Panel A-001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bagaimana cara Mengisolasi?</label>
                  <div className="space-y-2">
                    {['Posisi Handle / Tombol OFF', 'LOTO', 'Melepas Sekring', 'Melakukan Pengetesan tidak beroprasi'].map((option) => (
                      <label key={option} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={formData.isolasi_listrik.cara_mengisolasi.includes(option)}
                          onChange={() => {
                            const current = formData.isolasi_listrik.cara_mengisolasi;
                            const updated = current.includes(option)
                              ? current.filter(v => v !== option)
                              : [...current, option];
                            setFormData({
                              ...formData,
                              isolasi_listrik: { ...formData.isolasi_listrik, cara_mengisolasi: updated }
                            });
                          }}
                          className="w-4 h-4"
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                  <input
                    type="text"
                    value={formData.isolasi_listrik.cara_mengisolasi_lainnya}
                    onChange={(e) => setFormData({
                      ...formData,
                      isolasi_listrik: { ...formData.isolasi_listrik, cara_mengisolasi_lainnya: e.target.value }
                    })}
                    className="w-full p-2 border border-gray-300 rounded-lg mt-2"
                    placeholder="Lainnya (tuliskan)"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Isolasi Utilitas */}
          <div className="border border-gray-300 rounded-lg p-4">
            <label className="flex items-center gap-2 mb-4">
              <input
                type="checkbox"
                checked={formData.isolasi_utilitas.dilakukan}
                onChange={(e) => setFormData({
                  ...formData,
                  isolasi_utilitas: { ...formData.isolasi_utilitas, dilakukan: e.target.checked }
                })}
                className="w-4 h-4"
              />
              <span className="font-semibold">Isolasi Utilitas</span>
            </label>

            {formData.isolasi_utilitas.dilakukan && (
              <div className="space-y-4 ml-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Jenis Utilitas</label>
                  <div className="space-y-2">
                    {['Steam', 'Gas', 'Udara bertekanan', 'Air'].map((option) => (
                      <label key={option} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={formData.isolasi_utilitas.jenis_utilitas.includes(option)}
                          onChange={() => {
                            const current = formData.isolasi_utilitas.jenis_utilitas;
                            const updated = current.includes(option)
                              ? current.filter(v => v !== option)
                              : [...current, option];
                            setFormData({
                              ...formData,
                              isolasi_utilitas: { ...formData.isolasi_utilitas, jenis_utilitas: updated }
                            });
                          }}
                          className="w-4 h-4"
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={formData.isolasi_utilitas.jenis_utilitas.includes('Amonia')}
                      onChange={() => {
                        const current = formData.isolasi_utilitas.jenis_utilitas;
                        const updated = current.includes('Amonia')
                          ? current.filter(v => v !== 'Amonia')
                          : [...current, 'Amonia'];
                        setFormData({
                          ...formData,
                          isolasi_utilitas: { ...formData.isolasi_utilitas, jenis_utilitas: updated }
                        });
                      }}
                      className="w-4 h-4"
                    />
                    Ammonia
                  </label>

                  {formData.isolasi_utilitas.jenis_utilitas.includes('Amonia') && (
                    <div className="mt-4 space-y-4 border-l-4 border-red-400 pl-4">
                      {/* PERINGATAN: Amonia WAJIB Management Approval */}
                      <div className="mb-4 p-3 bg-red-50 border border-red-300 rounded-lg">
                        <p className="text-sm text-red-800 font-medium flex items-center gap-2">
                          ⚠️ PERINGATAN PENTING:
                        </p>
                        <p className="text-sm text-red-700 mt-1">
                          Karena memilih <strong>Amonia</strong> pada Isolasi Utilitas, 
                          approval dari <strong>Management (Leadership Team) WAJIB</strong> dipilih oleh PIC.
                        </p>
                        <p className="text-xs text-red-600 mt-2">
                          Pastikan PIC mengisi Management approver saat proses approval nanti.
                        </p>
                      </div>
                      {/* Form Informasi Permit */}
                      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-red-600 text-white px-4 py-2 font-semibold">
                          Informasi Permit
                        </div>
                        <div className="p-4 space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Jenis Pekerjaan</label>
                              <input
                                type="text"
                                value={formData.isolasi_utilitas.amonia_jenis_pekerjaan || ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  isolasi_utilitas: { 
                                    ...formData.isolasi_utilitas, 
                                    amonia_jenis_pekerjaan: e.target.value 
                                  }
                                })}
                                className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                                placeholder="Masukkan jenis pekerjaan"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Lokasi/Area</label>
                              <input
                                type="text"
                                value={formData.isolasi_utilitas.amonia_lokasi || ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  isolasi_utilitas: { 
                                    ...formData.isolasi_utilitas, 
                                    amonia_lokasi: e.target.value 
                                  }
                                })}
                                className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                                placeholder="Masukkan lokasi/area"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Nama Peralatan/Asset</label>
                              <input
                                type="text"
                                value={formData.isolasi_utilitas.amonia_nama_peralatan || ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  isolasi_utilitas: { 
                                    ...formData.isolasi_utilitas, 
                                    amonia_nama_peralatan: e.target.value 
                                  }
                                })}
                                className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                                placeholder="Masukkan nama peralatan/asset"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Pemilik Area</label>
                              <input
                                type="text"
                                value={formData.isolasi_utilitas.amonia_pemilik_area || ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  isolasi_utilitas: { 
                                    ...formData.isolasi_utilitas, 
                                    amonia_pemilik_area: e.target.value 
                                  }
                                })}
                                className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                                placeholder="Masukkan pemilik area"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Authorized LOTO Person</label>
                              <input
                                type="text"
                                value={formData.isolasi_utilitas.amonia_loto_person || ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  isolasi_utilitas: { 
                                    ...formData.isolasi_utilitas, 
                                    amonia_loto_person: e.target.value 
                                  }
                                })}
                                className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                                placeholder="Masukkan nama authorized LOTO person"
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="block text-sm font-medium text-gray-700 mb-1">Uraian Pekerjaan</label>
                              <textarea
                                value={formData.isolasi_utilitas.amonia_uraian_pekerjaan || ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  isolasi_utilitas: { 
                                    ...formData.isolasi_utilitas, 
                                    amonia_uraian_pekerjaan: e.target.value 
                                  }
                                })}
                                className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                                rows="2"
                                placeholder="Uraikan pekerjaan yang akan dilakukan"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Form Titik Isolasi */}
                      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-blue-600 text-white px-4 py-2 font-semibold">
                          Titik Isolasi
                        </div>
                        <div className="p-4 space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Jenis Energi</label>
                              <select
                                value={formData.isolasi_utilitas.amonia_jenis_energi || ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  isolasi_utilitas: { 
                                    ...formData.isolasi_utilitas, 
                                    amonia_jenis_energi: e.target.value 
                                  }
                                })}
                                className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                              >
                                <option value="">Pilih Jenis Energi</option>
                                <option value="Listrik">Listrik</option>
                                <option value="Pneumatik">Pneumatik</option>
                                <option value="Hidrolik">Hidrolik</option>
                                <option value="Kimia (Amonia)">Kimia (Amonia)</option>
                                <option value="Mekanik">Mekanik</option>
                                <option value="Thermal">Thermal</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Titik Isolasi</label>
                              <input
                                type="text"
                                value={formData.isolasi_utilitas.amonia_titik_isolasi || ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  isolasi_utilitas: { 
                                    ...formData.isolasi_utilitas, 
                                    amonia_titik_isolasi: e.target.value 
                                  }
                                })}
                                className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                                placeholder="Masukkan titik isolasi"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Metode Isolation</label>
                              <select
                                value={formData.isolasi_utilitas.amonia_metode_isolasi || ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  isolasi_utilitas: { 
                                    ...formData.isolasi_utilitas, 
                                    amonia_metode_isolasi: e.target.value 
                                  }
                                })}
                                className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                              >
                                <option value="">Pilih Metode Isolation</option>
                                <option value="LOTO (Lock Out Tag Out)">LOTO (Lock Out Tag Out)</option>
                                <option value="Valve Closed">Valve Closed</option>
                                <option value="Blind/Blank">Blind/Blank</option>
                                <option value="Double Block and Bleed">Double Block and Bleed</option>
                                <option value="Line Break">Line Break</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Peralatan Lock</label>
                              <input
                                type="text"
                                value={formData.isolasi_utilitas.amonia_peralatan_lock || ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  isolasi_utilitas: { 
                                    ...formData.isolasi_utilitas, 
                                    amonia_peralatan_lock: e.target.value 
                                  }
                                })}
                                className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                                placeholder="Masukkan peralatan lock yang digunakan"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Form APD */}
                      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-green-600 text-white px-4 py-2 font-semibold">
                          APD (Alat Pelindung Diri)
                        </div>
                        <div className="p-4">
                          <div className="grid grid-cols-3 gap-4">
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={formData.isolasi_utilitas.amonia_apd_helmet || false}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  isolasi_utilitas: { 
                                    ...formData.isolasi_utilitas, 
                                    amonia_apd_helmet: e.target.checked 
                                  }
                                })}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Helmet</span>
                            </label>
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={formData.isolasi_utilitas.amonia_apd_sarung_tangan || false}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  isolasi_utilitas: { 
                                    ...formData.isolasi_utilitas, 
                                    amonia_apd_sarung_tangan: e.target.checked 
                                  }
                                })}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Sarung Tangan</span>
                            </label>
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={formData.isolasi_utilitas.amonia_apd_respirator || false}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  isolasi_utilitas: { 
                                    ...formData.isolasi_utilitas, 
                                    amonia_apd_respirator: e.target.checked 
                                  }
                                })}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Respirator</span>
                            </label>
                          </div>
                          <div className="mt-3">
                            <input
                              type="text"
                              value={formData.isolasi_utilitas.amonia_apd_lainnya || ''}
                              onChange={(e) => setFormData({
                                ...formData,
                                isolasi_utilitas: { 
                                  ...formData.isolasi_utilitas, 
                                  amonia_apd_lainnya: e.target.value 
                                }
                              })}
                              className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                              placeholder="APD Lainnya (tuliskan)"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <input
                    type="text"
                    value={formData.isolasi_utilitas.jenis_utilitas_lainnya}
                    onChange={(e) => setFormData({
                      ...formData,
                      isolasi_utilitas: { ...formData.isolasi_utilitas, jenis_utilitas_lainnya: e.target.value }
                    })}
                    className="w-full p-2 border border-gray-300 rounded-lg mt-2"
                    placeholder="Lainnya (tuliskan)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Cara Mengisolasi</label>
                  <div className="space-y-2">
                    {['Menurunkan Tekanan', 'Menutup Kran (Valve)', 'LOTO'].map((option) => (
                      <label key={option} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={formData.isolasi_utilitas.cara_mengisolasi.includes(option)}
                          onChange={() => {
                            const current = formData.isolasi_utilitas.cara_mengisolasi;
                            const updated = current.includes(option)
                              ? current.filter(v => v !== option)
                              : [...current, option];
                            setFormData({
                              ...formData,
                              isolasi_utilitas: { ...formData.isolasi_utilitas, cara_mengisolasi: updated }
                            });
                          }}
                          className="w-4 h-4"
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                  <input
                    type="text"
                    value={formData.isolasi_utilitas.cara_mengisolasi_lainnya}
                    onChange={(e) => setFormData({
                      ...formData,
                      isolasi_utilitas: { ...formData.isolasi_utilitas, cara_mengisolasi_lainnya: e.target.value }
                    })}
                    className="w-full p-2 border border-gray-300 rounded-lg mt-2"
                    placeholder="Lainnya (tuliskan)"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Isolasi Proses */}
          <div className="border border-gray-300 rounded-lg p-4">
            <label className="flex items-center gap-2 mb-4">
              <input
                type="checkbox"
                checked={formData.isolasi_proses.dilakukan}
                onChange={(e) => setFormData({
                  ...formData,
                  isolasi_proses: { ...formData.isolasi_proses, dilakukan: e.target.checked }
                })}
                className="w-4 h-4"
              />
              <span className="font-semibold">Isolasi Proses</span>
            </label>

            {formData.isolasi_proses.dilakukan && (
              <div className="space-y-4 ml-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Melibatkan Peralatan</label>
                  <input
                    type="text"
                    value={formData.isolasi_proses.peralatan}
                    onChange={(e) => setFormData({
                      ...formData,
                      isolasi_proses: { ...formData.isolasi_proses, peralatan: e.target.value }
                    })}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    placeholder="Sebutkan peralatan"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bagaimana Cara Mengisolasi</label>
                  <div className="space-y-2">
                    {['Mengosongkan Lokasi', 'Memuluskan Saluran', 'Mengosongkan Material', 'Membilas (Flush)'].map((option) => (
                      <label key={option} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={formData.isolasi_proses.cara_mengisolasi.includes(option)}
                          onChange={() => {
                            const current = formData.isolasi_proses.cara_mengisolasi;
                            const updated = current.includes(option)
                              ? current.filter(v => v !== option)
                              : [...current, option];
                            setFormData({
                              ...formData,
                              isolasi_proses: { ...formData.isolasi_proses, cara_mengisolasi: updated }
                            });
                          }}
                          className="w-4 h-4"
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                  <input
                    type="text"
                    value={formData.isolasi_proses.cara_mengisolasi_lainnya}
                    onChange={(e) => setFormData({
                      ...formData,
                      isolasi_proses: { ...formData.isolasi_proses, cara_mengisolasi_lainnya: e.target.value }
                    })}
                    className="w-full p-2 border border-gray-300 rounded-lg mt-2"
                    placeholder="Lainnya (tuliskan)"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Step: Pencegahan
    if (stepName === 'Pencegahan') {
      return (
        <div className="space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Daftar Pencegahan Umum</h2>
            <div className="grid grid-cols-2 gap-2">
              {pencegahanUmumOptions.map((option) => (
                <label key={option} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={formData.daftar_pencegahan_umum.includes(option)}
                    onChange={() => toggleCheckbox('daftar_pencegahan_umum', option)}
                    className="w-4 h-4"
                  />
                  {option}
                </label>
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Daftar Pencegahan Penggalian</h2>
            <div className="grid grid-cols-2 gap-2">
              {['Permit Penggalian', 'Peta Utilitas Underground', 'Hand Digging', 'Barricade Area'].map((option) => (
                <label key={option} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={formData.daftar_pencegahan_penggalian.includes(option)}
                    onChange={() => toggleCheckbox('daftar_pencegahan_penggalian', option)}
                    className="w-4 h-4"
                  />
                  {option}
                </label>
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Pencegahan Lainnya (Optional)</h2>
            <textarea
              value={formData.daftar_pencegahan_lainnya.join('\n')}
              onChange={(e) => setFormData({ ...formData, daftar_pencegahan_lainnya: e.target.value.split('\n') })}
              className="w-full p-3 border border-gray-300 rounded-lg"
              rows="4"
              placeholder="Tuliskan pencegahan tambahan (satu per baris)"
            />
          </div>
        </div>
      );
    }

    // Step: Review
    if (stepName === 'Review') {
      return (
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Review & Submit</h2>

          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="font-semibold text-blue-900 mb-2">📋 Ringkasan PTW</h3>
            <div className="space-y-1 text-sm text-blue-800">
              <p><strong>Lokasi:</strong> {formData.lokasi_pekerjaan}</p>
              <p><strong>Deskripsi:</strong> {formData.deskripsi_pekerjaan}</p>
              <p><strong>Tanggal Mulai:</strong> {formData.tanggal_mulai}</p>
              <p><strong>Jumlah Pekerja:</strong> {formData.nama_pekerja.filter(n => n.trim()).length}</p>
              {formData.high_risk_activities.length > 0 && (
                <p><strong>High Risk:</strong> {formData.high_risk_activities.join(', ')}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Pilih PIC (Person In Charge) <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-gray-600 mb-2">PIC akan melakukan review dan memilih approver selanjutnya</p>
            <select
              value={formData.pic_id}
              onChange={(e) => setFormData({ ...formData, pic_id: e.target.value })}
              className={getInputClass('pic_id')}
            >
              <option value="">-- Pilih PIC --</option>
              {picUsers.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.email})
                </option>
              ))}
            </select>
            {validationErrors.pic_id && (
              <p className="text-red-500 text-xs mt-1">{validationErrors.pic_id}</p>
            )}
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <p className="text-sm text-yellow-800">
              ⚠️ <strong>Perhatian:</strong> Setelah submit, PTW akan dikirim ke PIC untuk review.
              {formData.high_risk_activities.length > 0 && (
                <span> Karena Anda memilih high risk activities, Anda akan diminta mengisi form detail high risk setelah ini.</span>
              )}
            </p>
          </div>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="w-full py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-400 flex items-center justify-center gap-2"
          >
            {loading ? (
              <><span className="animate-spin">⏳</span> Submitting...</>
            ) : (
              <><CheckCircle size={20} /> Submit PTW</>
            )}
          </button>
        </div>
      );
    }
  };

  const renderHighRiskFormsContent = () => {
    return (
      <div className="space-y-8">
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
          <p className="text-sm text-blue-800 font-medium">
            📋 PTW telah dibuat! Sekarang silakan lengkapi form detail untuk high risk activities yang Anda pilih: <strong>{formData.high_risk_activities.join(', ')}</strong>
          </p>
        </div>

        {/* Error Summary untuk High Risk Forms */}
        {Object.keys(validationErrors).length > 0 && (
          <ValidationErrorSummary errors={validationErrors} />
        )}

        {/* WORKING AT HEIGHT FORM - COMPLETE */}
        {formData.high_risk_activities.includes('Working at Height') && (
          <div className="border border-red-300 rounded-lg p-6 bg-red-50">
            <h3 className="text-lg font-bold text-red-900 mb-4">🏗️ WORKING AT HEIGHT PERMIT</h3>
            <p className="text-sm text-gray-700 mb-4">
              Pekerjaan yang terdapat bahaya bekerja di ketinggian, wajib mendapatkan approval Work Permit Bekerja di Ketinggian
            </p>

            {!formData.high_risk_forms.working_at_height && (
              <button
                type="button"
                onClick={() => initializeHighRiskForm('working_at_height')}
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold"
              >
                📝 Mulai Isi Form Working at Height
              </button>
            )}

            {formData.high_risk_forms.working_at_height && (
              <div className="space-y-6">
                {/* Section 1: Disclaimer */}
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">1. Disclaimer</h4>
                  <label className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={formData.high_risk_forms.working_at_height.workers_trained_disclaimer}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          high_risk_forms: {
                            ...formData.high_risk_forms,
                            working_at_height: {
                              ...formData.high_risk_forms.working_at_height,
                              workers_trained_disclaimer: e.target.checked
                            }
                          }
                        });
                      }}
                      className="mt-1 w-5 h-5"
                    />
                    <span className="text-sm">
                      <strong className="text-red-600">*</strong> Saya menyatakan pekerja yang didaftarkan telah ditraining bekerja di ketinggian dan dalam kondisi sehat
                    </span>
                  </label>
                  {validationErrors.wah_disclaimer && (
                    <p className="text-xs text-red-600 mt-1">⚠️ {validationErrors.wah_disclaimer}</p>
                  )}
                </div>

                {/* TABEL TENSI PEKERJA */}
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">1B. Data Kesehatan Pekerja (Tensi & Keluhan)</h4>
                  <p className="text-xs text-gray-600 mb-3">
                    Data tensi dan keluhan pekerja wajib diisi untuk pekerjaan di ketinggian
                  </p>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-300">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="border border-gray-300 p-2 text-left text-sm">Nama Pekerja</th>
                          <th className="border border-gray-300 p-2 text-left text-sm">Tekanan Darah (Tensi) <span className="text-red-500">*</span></th>
                          <th className="border border-gray-300 p-2 text-left text-sm">Tanggal Pemeriksaan</th>
                          <th className="border border-gray-300 p-2 text-left text-sm">Keluhan</th>
                          <th className="border border-gray-300 p-2 text-left text-sm">Tindak Lanjut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.nama_pekerja && formData.nama_pekerja.map((nama, idx) => {
                          const existingData = tensiPekerja.find(p => p.id === idx) || {
                            tekanan_darah: '',
                            tanggal_pemeriksaan: '',
                            keluhan: '',
                            tindak_lanjut: ''
                          };
                          
                          const hasError = validationErrors[`wah_tensi_${idx}`];
                          
                          return (
                            <tr key={idx}>
                              <td className="border border-gray-300 p-2 text-sm bg-gray-50">
                                {nama || '-'}
                              </td>
                              <td className="border border-gray-300 p-2">
                                <input
                                  type="text"
                                  value={existingData.tekanan_darah}
                                  onChange={(e) => updateTensiPekerja(idx, 'tekanan_darah', e.target.value)}
                                  className={`w-full p-1 border rounded text-sm ${hasError ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                                  placeholder="Contoh: 120/80"
                                />
                                {hasError && (
                                  <p className="text-red-500 text-xs mt-1">{validationErrors[`wah_tensi_${idx}`]}</p>
                                )}
                              </td>
                              <td className="border border-gray-300 p-2">
                                <input
                                  type="date"
                                  value={existingData.tanggal_pemeriksaan}
                                  onChange={(e) => updateTensiPekerja(idx, 'tanggal_pemeriksaan', e.target.value)}
                                  className="w-full p-1 border border-gray-300 rounded text-sm"
                                />
                              </td>
                              <td className="border border-gray-300 p-2">
                                <input
                                  type="text"
                                  value={existingData.keluhan}
                                  onChange={(e) => updateTensiPekerja(idx, 'keluhan', e.target.value)}
                                  className="w-full p-1 border border-gray-300 rounded text-sm"
                                  placeholder="Pusing, mual, dll"
                                />
                              </td>
                              <td className="border border-gray-300 p-2">
                                <input
                                  type="text"
                                  value={existingData.tindak_lanjut}
                                  onChange={(e) => updateTensiPekerja(idx, 'tindak_lanjut', e.target.value)}
                                  className="w-full p-1 border border-gray-300 rounded text-sm"
                                  placeholder="Istirahat, rujuk ke dokter, dll"
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Section 2: Power Supply */}
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">2. Pengecekan Instalasi Listrik/Power Supply</h4>
                  <p className="text-xs text-gray-600 mb-3">Area tempat bekerja harus bebas dari jalur suplai Listrik, sekurang-kurangnya 4m</p>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">Area bebas dari listrik? <span className="text-red-500">*</span></label>
                      <select
                        value={formData.high_risk_forms.working_at_height.power_supply_area_clear}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            high_risk_forms: {
                              ...formData.high_risk_forms,
                              working_at_height: {
                                ...formData.high_risk_forms.working_at_height,
                                power_supply_area_clear: e.target.value
                              }
                            }
                          });
                        }}
                        className={`w-full p-2 border rounded ${validationErrors.wah_power_supply ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                      >
                        <option value="">Pilih</option>
                        <option value="Ya">Ya</option>
                        <option value="Tidak">Tidak</option>
                      </select>
                      {validationErrors.wah_power_supply && (
                        <p className="text-red-500 text-xs mt-1">{validationErrors.wah_power_supply}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Catatan:</label>
                      <input
                        type="text"
                        value={formData.high_risk_forms.working_at_height.power_supply_notes}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            high_risk_forms: {
                              ...formData.high_risk_forms,
                              working_at_height: {
                                ...formData.high_risk_forms.working_at_height,
                                power_supply_notes: e.target.value
                              }
                            }
                          });
                        }}
                        className="w-full p-2 border border-gray-300 rounded"
                        placeholder="Catatan tambahan"
                      />
                    </div>
                  </div>
                </div>

                {/* Section 3: Ladder Checks - Simplified for brevity, but with error handling */}
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">3. Pengecekan Alat Bantu Tangga/Ladders</h4>
                  <div className="space-y-3">
                    {[
                      { field: 'ladder_condition_good', label: 'Tangga dalam keadaan baik?' },
                      { field: 'area_safe_movement', label: 'Area aman untuk pergerakan orang?' },
                      { field: 'aluminum_ladder_electric_area', label: 'Tangga aluminum TIDAK digunakan di area listrik?' },
                      { field: 'equipment_belt_attached', label: 'Peralatan terpasang dengan belt penumpu?' },
                      { field: 'ladder_pedestrian_positioned', label: 'Tangga dipasang dengan posisi tepat di jalur orang?' },
                      { field: 'ladder_lock_secured', label: 'Pengunci tangga terpasang benar?' },
                      { field: 'ladder_extension_proper', label: 'Panjang ekstensi tangga max 1 meter?' }
                    ].map((item) => (
                      <div key={item.field}>
                        <label className="block text-sm font-medium mb-1">{item.label} <span className="text-red-500">*</span></label>
                        <select
                          value={formData.high_risk_forms.working_at_height[item.field]}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              high_risk_forms: {
                                ...formData.high_risk_forms,
                                working_at_height: {
                                  ...formData.high_risk_forms.working_at_height,
                                  [item.field]: e.target.value
                                }
                              }
                            });
                          }}
                          className={`w-full p-2 border rounded ${validationErrors[`wah_${item.field}`] ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                        >
                          <option value="">Pilih</option>
                          <option value="Ya">Ya</option>
                          <option value="Tidak">Tidak</option>
                        </select>
                        {validationErrors[`wah_${item.field}`] && (
                          <p className="text-red-500 text-xs mt-1">{validationErrors[`wah_${item.field}`]}</p>
                        )}
                      </div>
                    ))}
                    <div>
                      <label className="block text-sm font-medium mb-1">Catatan Ladder:</label>
                      <textarea
                        value={formData.high_risk_forms.working_at_height.ladder_notes}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            high_risk_forms: {
                              ...formData.high_risk_forms,
                              working_at_height: {
                                ...formData.high_risk_forms.working_at_height,
                                ladder_notes: e.target.value
                              }
                            }
                          });
                        }}
                        className="w-full p-2 border border-gray-300 rounded"
                        rows="2"
                        placeholder="Catatan tambahan untuk ladder checks"
                      />
                    </div>
                  </div>
                </div>

                {/* Section 4: PPE */}
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">4. Alat Pelindung Diri (PPE)</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { field: 'ppe_safety_belt_harness', label: 'Safety Belt/Harness/Life line' },
                      { field: 'ppe_goggles', label: 'Kacamata Pelindung (Goggles)' },
                      { field: 'ppe_gloves', label: 'Sarung Tangan (Gloves)' },
                      { field: 'ppe_safety_boots', label: 'Sepatu Safety' },
                      { field: 'ppe_protective_clothing', label: 'Pakaian Kerja/Pelindung' },
                      { field: 'ppe_safety_helmet', label: 'Pelindung Kepala (Safety Helmet)' }
                    ].map((item) => (
                      <label key={item.field} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.high_risk_forms.working_at_height[item.field]}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              high_risk_forms: {
                                ...formData.high_risk_forms,
                                working_at_height: {
                                  ...formData.high_risk_forms.working_at_height,
                                  [item.field]: e.target.checked
                                }
                              }
                            });
                          }}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">{item.label}</span>
                      </label>
                    ))}
                  </div>
                  <div className="mt-3">
                    <label className="block text-sm font-medium mb-1">Lainnya (tuliskan):</label>
                    <input
                      type="text"
                      value={formData.high_risk_forms.working_at_height.ppe_other}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          high_risk_forms: {
                            ...formData.high_risk_forms,
                            working_at_height: {
                              ...formData.high_risk_forms.working_at_height,
                              ppe_other: e.target.value
                            }
                          }
                        });
                      }}
                      className="w-full p-2 border border-gray-300 rounded"
                      placeholder="PPE lainnya"
                    />
                  </div>
                </div>

                {/* Section 5: Forklift & Platform - Simplified for brevity */}
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">5. Alat Bantu Lainnya (Forklift dan Platform Sementara)</h4>
                  <div className="space-y-3">
                    {[
                      { field: 'forklift_platform_licensed', label: 'Semua peralatan platform bergerak memiliki ijin?' },
                      { field: 'forklift_area_clear_electric', label: 'Area bebas dari kabel listrik?' },
                      { field: 'forklift_fall_harness_used', label: 'Fall safety harness dipakai?' },
                      { field: 'forklift_mobile_platform_osha', label: 'Mobile platform sesuai OSHA 1926.502?' },
                      { field: 'scaffolding_osha_compliant', label: 'Scaffolding comply dengan OSHA 1910.20?' },
                      { field: 'scaffolding_toe_guard_rails', label: 'Scaffolding dilengkapi toeboard dan guard rails?' },
                      { field: 'mobile_scaffolding_locked', label: 'Mobile scaffolding dikunci saat digunakan?' },
                      { field: 'outrigger_installed', label: 'Outrigger dipasang untuk stabilitas?' },
                      { field: 'work_area_clear_electric', label: 'Area kerja bebas dari kabel listrik?' }
                    ].map((item) => (
                      <div key={item.field}>
                        <label className="block text-sm font-medium mb-1">{item.label} <span className="text-red-500">*</span></label>
                        <select
                          value={formData.high_risk_forms.working_at_height[item.field]}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              high_risk_forms: {
                                ...formData.high_risk_forms,
                                working_at_height: {
                                  ...formData.high_risk_forms.working_at_height,
                                  [item.field]: e.target.value
                                }
                              }
                            });
                          }}
                          className={`w-full p-2 border rounded ${validationErrors[`wah_${item.field}`] ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                        >
                          <option value="">Pilih</option>
                          <option value="Ya">Ya</option>
                          <option value="Tidak">Tidak</option>
                          <option value="N/A">N/A</option>
                        </select>
                        {validationErrors[`wah_${item.field}`] && (
                          <p className="text-red-500 text-xs mt-1">{validationErrors[`wah_${item.field}`]}</p>
                        )}
                      </div>
                    ))}
                    <div>
                      <label className="block text-sm font-medium mb-1">Catatan Forklift/Platform:</label>
                      <textarea
                        value={formData.high_risk_forms.working_at_height.forklift_notes}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            high_risk_forms: {
                              ...formData.high_risk_forms,
                              working_at_height: {
                                ...formData.high_risk_forms.working_at_height,
                                forklift_notes: e.target.value
                              }
                            }
                          });
                        }}
                        className="w-full p-2 border border-gray-300 rounded"
                        rows="2"
                        placeholder="Catatan tambahan"
                      />
                    </div>
                  </div>
                </div>

                {/* Section 6: Precautions */}
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">6. Pemberitahuan (Precaution)</h4>
                  <p className="text-xs text-gray-600 mb-3">Pemberitahuan lapangan yang telah dilakukan:</p>
                  <div className="space-y-3">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.high_risk_forms.working_at_height.precaution_barricade}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            high_risk_forms: {
                              ...formData.high_risk_forms,
                              working_at_height: {
                                ...formData.high_risk_forms.working_at_height,
                                precaution_barricade: e.target.checked
                              }
                            }
                          });
                        }}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Papan Peringatan/Garis Lintasan (Barricade) <span className="text-red-500">*</span></span>
                    </label>
                    {validationErrors.wah_barricade && (
                      <p className="text-red-500 text-xs">{validationErrors.wah_barricade}</p>
                    )}
                    <div>
                      <label className="block text-sm font-medium mb-1">Informasi Terkait Hasil Tensi Pekerja (wajib isi): <span className="text-red-500">*</span></label>
                      <textarea
                        value={formData.high_risk_forms.working_at_height.precaution_special_warning}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            high_risk_forms: {
                              ...formData.high_risk_forms,
                              working_at_height: {
                                ...formData.high_risk_forms.working_at_height,
                                precaution_special_warning: e.target.value
                              }
                            }
                          });
                        }}
                        className={`w-full p-2 border rounded ${validationErrors.wah_special_warning ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                        rows="2"
                        placeholder="Pekerja1: 120/60, Pekerja2: 110/65, dst"
                      />
                      {validationErrors.wah_special_warning && (
                        <p className="text-red-500 text-xs mt-1">{validationErrors.wah_special_warning}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Guidelines Acknowledgment */}
                <div className="bg-yellow-50 border-2 border-yellow-300 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">📜 PROSEDUR PERMINTAAN IJIN BEKERJA DI KETINGGIAN</h4>
                  <div className="bg-white p-3 rounded border border-gray-300 max-h-40 overflow-y-auto mb-3">
                    <div className="text-xs text-gray-700 space-y-2">
                      <p><strong>Disclaimer:</strong> Saya sudah memahami dan mematuhi prosedur bekerja di ketinggian yang sudah dituliskan. Jika terdapat ketidaksesuaian, kontraktor akan menerima konsekuensi yang berlaku</p>
                    </div>
                  </div>
                  <label className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={formData.high_risk_forms.working_at_height.guidelines_acknowledged}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          high_risk_forms: {
                            ...formData.high_risk_forms,
                            working_at_height: {
                              ...formData.high_risk_forms.working_at_height,
                              guidelines_acknowledged: e.target.checked
                            }
                          }
                        });
                      }}
                      className="mt-1 w-5 h-5"
                    />
                    <span className="text-sm">
                      <strong className="text-red-600">*</strong> Saya Setuju
                    </span>
                  </label>
                  {validationErrors.wah_guidelines && (
                    <p className="text-xs text-red-600 mt-1">⚠️ {validationErrors.wah_guidelines}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* HOT WORK FORM - COMPLETE */}
        {formData.high_risk_activities.includes('Hot Work') && (
          <div className="border border-orange-300 rounded-lg p-6 bg-orange-50">
            <h3 className="text-lg font-bold text-orange-900 mb-4">🔥 HOT WORK PERMIT</h3>
            
            {!formData.high_risk_forms.hot_work && (
              <button
                type="button"
                onClick={() => initializeHighRiskForm('hot_work')}
                className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-semibold"
              >
                📝 Mulai Isi Form Hot Work
              </button>
            )}

            {formData.high_risk_forms.hot_work && (
              <div className="space-y-6">
                {/* Section 1: Work Type */}
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">1. Jenis Pekerjaan Hot Works</h4>
                  <div className="space-y-2">
                    {[
                      { field: 'work_type_welding_brazing', label: 'Pengelasan/Brazing/Welding' },
                      { field: 'work_type_cutting', label: 'Pemotongan/Cutting Works' },
                      { field: 'work_type_grinding', label: 'Grinding' },
                      { field: 'work_type_soldering', label: 'Soldering' },
                      { field: 'work_type_thawing', label: 'Thawing' }
                    ].map((item) => (
                      <label key={item.field} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.high_risk_forms.hot_work[item.field]}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              high_risk_forms: {
                                ...formData.high_risk_forms,
                                hot_work: {
                                  ...formData.high_risk_forms.hot_work,
                                  [item.field]: e.target.checked
                                }
                              }
                            });
                          }}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">{item.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Section 2: Hot Works Checklist */}
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">2. Hot Works Checklist</h4>
                  <div className="space-y-3">
                    {[
                      { field: 'checklist_sprinkler_hydrant', label: 'Springkler/Hydrant dalam keadaan?' },
                      { field: 'checklist_equipment_condition', label: 'Peralatan Hot Works dalam keadaan baik?' },
                      { field: 'checklist_fire_extinguisher', label: 'APK ada dilapangan dan dalam kondisi baik?' }
                    ].map((item) => (
                      <div key={item.field}>
                        <label className="block text-sm font-medium mb-1">{item.label} <span className="text-red-500">*</span></label>
                        <select
                          value={formData.high_risk_forms.hot_work[item.field]}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              high_risk_forms: {
                                ...formData.high_risk_forms,
                                hot_work: {
                                  ...formData.high_risk_forms.hot_work,
                                  [item.field]: e.target.value
                                }
                              }
                            });
                          }}
                          className={`w-full p-2 border rounded ${validationErrors[`hw_${item.field}`] ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                        >
                          <option value="">Pilih</option>
                          <option value="Baik">Baik</option>
                          <option value="Rusak">Rusak</option>
                        </select>
                        {validationErrors[`hw_${item.field}`] && (
                          <p className="text-red-500 text-xs mt-1">{validationErrors[`hw_${item.field}`]}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Section 3: Area 12m */}
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">3. Area bebas 12 meter dari lokasi</h4>
                  <div className="space-y-3">
                    {[
                      { field: 'area_12m_material_cleared', label: 'Material yang akan dikerjakan sudah bebas dari debu, debris, flammable liquids?' },
                      { field: 'area_12m_no_explosive', label: 'Kondisi lapangan bebas dari bahan yang bisa meledak?' },
                      { field: 'area_12m_floor_protected', label: 'Lantai kerja sudah dilapisi dengan karung basah berisi pasir atau Fire Blanket?' },
                      { field: 'area_12m_walls_openings_protected', label: 'Semua dinding dan bukaan lantai (opening) sudah dilindungi dari percikan api?' },
                      { field: 'area_12m_passage_closed', label: 'Jalur orang/material dibawah tempat pengelasan sudah ditutup?' }
                    ].map((item) => (
                      <div key={item.field}>
                        <label className="block text-sm font-medium mb-1">{item.label} <span className="text-red-500">*</span></label>
                        <select
                          value={formData.high_risk_forms.hot_work[item.field]}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              high_risk_forms: {
                                ...formData.high_risk_forms,
                                hot_work: {
                                  ...formData.high_risk_forms.hot_work,
                                  [item.field]: e.target.value
                                }
                              }
                            });
                          }}
                          className={`w-full p-2 border rounded ${validationErrors[`hw_${item.field}`] ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                        >
                          <option value="">Pilih</option>
                          <option value="Ya">Ya</option>
                          <option value="Tidak">Tidak</option>
                        </select>
                        {validationErrors[`hw_${item.field}`] && (
                          <p className="text-red-500 text-xs mt-1">{validationErrors[`hw_${item.field}`]}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Section 4: Wall/Ceiling */}
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">4. Hot Work Dinding/Ceiling/Plafon</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">Material dinding/Ceiling dapat terbakar (Combustible) terbuat dari PU, EPS? <span className="text-red-500">*</span></label>
                      <select
                        value={formData.high_risk_forms.hot_work.wall_ceiling_combustible}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            high_risk_forms: {
                              ...formData.high_risk_forms,
                              hot_work: {
                                ...formData.high_risk_forms.hot_work,
                                wall_ceiling_combustible: e.target.value
                              }
                            }
                          });
                        }}
                        className={`w-full p-2 border rounded ${validationErrors.hw_wall_ceiling_combustible ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                      >
                        <option value="">Pilih</option>
                        <option value="Ya">Ya</option>
                        <option value="Tidak">Tidak</option>
                        <option value="N/A">N/A</option>
                      </select>
                      {validationErrors.hw_wall_ceiling_combustible && (
                        <p className="text-red-500 text-xs mt-1">{validationErrors.hw_wall_ceiling_combustible}</p>
                      )}
                    </div>
                    {formData.high_risk_forms.hot_work.wall_ceiling_combustible === 'Ya' && (
                      <div>
                        <label className="block text-sm font-medium mb-1">Jika Ya, sudah diisolasi dengan Fire Blanket atau fasilitas penampungan percikan? <span className="text-red-500">*</span></label>
                        <select
                          value={formData.high_risk_forms.hot_work.wall_ceiling_isolated}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              high_risk_forms: {
                                ...formData.high_risk_forms,
                                hot_work: {
                                  ...formData.high_risk_forms.hot_work,
                                  wall_ceiling_isolated: e.target.value
                                }
                              }
                            });
                          }}
                          className={`w-full p-2 border rounded ${validationErrors.hw_wall_ceiling_isolated ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                        >
                          <option value="">Pilih</option>
                          <option value="Ya">Ya</option>
                          <option value="Tidak">Tidak</option>
                          <option value="N/A">N/A</option>
                        </select>
                        {validationErrors.hw_wall_ceiling_isolated && (
                          <p className="text-red-500 text-xs mt-1">{validationErrors.hw_wall_ceiling_isolated}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Section 5: Confined Space */}
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">5. Hot Work Confined Space</h4>
                  <div className="space-y-3">
                    {[
                      { field: 'confined_space_cleaned', label: 'Confined Space sudah dibersihkan dari semua bahan yang bisa terbakar?' },
                      { field: 'confined_space_container_emptied', label: 'Container sudah dikosongkan sebelumnya?' },
                      { field: 'confined_space_permit_used', label: 'MENGGUNAKAN PERMIT TO WORK untuk confined space?' }
                    ].map((item) => (
                      <div key={item.field}>
                        <label className="block text-sm font-medium mb-1">{item.label}</label>
                        <select
                          value={formData.high_risk_forms.hot_work[item.field]}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              high_risk_forms: {
                                ...formData.high_risk_forms,
                                hot_work: {
                                  ...formData.high_risk_forms.hot_work,
                                  [item.field]: e.target.value
                                }
                              }
                            });
                          }}
                          className="w-full p-2 border border-gray-300 rounded"
                        >
                          <option value="">Pilih</option>
                          <option value="Ya">Ya</option>
                          <option value="Tidak">Tidak</option>
                          <option value="N/A">N/A</option>
                        </select>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Section 6: Area Monitoring */}
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">6. Hot Work Area Monitoring</h4>
                  <div className="space-y-3">
                    {[
                      { field: 'monitoring_fire_watch_2hrs', label: 'Pengecekan api (Fire Watch) setelah hot work? dilakukan sekurang-kurangnya 2 jam dan 4 jam apabila material dinding/ceiling dapat terbakar (Combustible) terbuat dari PU/EPS' },
                      { field: 'monitoring_she_has_extinguisher', label: 'SHE supervisor mempunyai satu unit APK sendiri?' },
                      { field: 'monitoring_she_trained', label: 'SHE supervisor sudah terlatih dalam penggunaan APK dan tahu lokasi hydrant?' }
                    ].map((item) => (
                      <div key={item.field}>
                        <label className="block text-sm font-medium mb-1">{item.label} <span className="text-red-500">*</span></label>
                        <select
                          value={formData.high_risk_forms.hot_work[item.field]}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              high_risk_forms: {
                                ...formData.high_risk_forms,
                                hot_work: {
                                  ...formData.high_risk_forms.hot_work,
                                  [item.field]: e.target.value
                                }
                              }
                            });
                          }}
                          className={`w-full p-2 border rounded ${validationErrors[`hw_${item.field}`] ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                        >
                          <option value="">Pilih</option>
                          <option value="Ya">Ya</option>
                          <option value="Tidak">Tidak</option>
                        </select>
                        {validationErrors[`hw_${item.field}`] && (
                          <p className="text-red-500 text-xs mt-1">{validationErrors[`hw_${item.field}`]}</p>
                        )}
                      </div>
                    ))}
                    <div>
                      <label className="block text-sm font-medium mb-1">SHE supervisor diperlukan pada bagian khusus pekerjaan (dinding/ceiling)?</label>
                      <select
                        value={formData.high_risk_forms.hot_work.monitoring_she_required_special}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            high_risk_forms: {
                              ...formData.high_risk_forms,
                              hot_work: {
                                ...formData.high_risk_forms.hot_work,
                                monitoring_she_required_special: e.target.value
                              }
                            }
                          });
                        }}
                        className="w-full p-2 border border-gray-300 rounded"
                      >
                        <option value="">Pilih</option>
                        <option value="Ya">Ya</option>
                        <option value="Tidak">Tidak</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Section 7: Other Aspects */}
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">7. Aspek Lainnya</h4>
                  <textarea
                    value={formData.high_risk_forms.hot_work.other_aspects}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        high_risk_forms: {
                          ...formData.high_risk_forms,
                          hot_work: {
                            ...formData.high_risk_forms.hot_work,
                            other_aspects: e.target.value
                          }
                        }
                      });
                    }}
                    className="w-full p-2 border border-gray-300 rounded"
                    rows="3"
                    placeholder="Tuliskan aspek lainnya yang perlu diperhatikan"
                  />
                </div>

                {/* Section 8: Disclaimer */}
                <div className="bg-yellow-50 border-2 border-yellow-300 p-4 rounded-lg">
                  <label className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={formData.high_risk_forms.hot_work.warning_sign_disclaimer}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          high_risk_forms: {
                            ...formData.high_risk_forms,
                            hot_work: {
                              ...formData.high_risk_forms.hot_work,
                              warning_sign_disclaimer: e.target.checked
                            }
                          }
                        });
                      }}
                      className="mt-1 w-5 h-5"
                    />
                    <span className="text-sm">
                      <strong className="text-red-600">*</strong> Saya akan membuat rambu Peringatan saat pekerjaan sedang berjalan
                    </span>
                  </label>
                  {validationErrors.hw_disclaimer && (
                    <p className="text-xs text-red-600 mt-1">⚠️ {validationErrors.hw_disclaimer}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* ELECTRIC MEDIUM VOLTAGE FORM */}
        {formData.high_risk_activities.includes('Electric Medium Voltage') && (
          <div className="border border-purple-300 rounded-lg p-6 bg-purple-50">
            <h3 className="text-lg font-bold text-purple-900 mb-4">⚡ ELECTRIC MEDIUM VOLTAGE PERMIT</h3>
            <p className="text-sm text-gray-700 mb-4">
              Izin kerja untuk pekerjaan yang berkaitan dengan Tegangan Menengah (MV) antara 1.000 V - 69.000 V
            </p>
            
            {!formData.high_risk_forms.electric_medium_voltage && (
              <button
                type="button"
                onClick={() => initializeHighRiskForm('electric_medium_voltage')}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold"
              >
                📝 Mulai Isi Form Electric Medium Voltage
              </button>
            )}

            {formData.high_risk_forms.electric_medium_voltage && (
              <div className="space-y-6">
                {/* Section 1: Disclaimer */}
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">1. Disclaimer</h4>
                  <label className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={formData.high_risk_forms.electric_medium_voltage.workers_briefing_disclaimer}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          high_risk_forms: {
                            ...formData.high_risk_forms,
                            electric_medium_voltage: {
                              ...formData.high_risk_forms.electric_medium_voltage,
                              workers_briefing_disclaimer: e.target.checked
                            }
                          }
                        });
                      }}
                      className="mt-1 w-5 h-5"
                    />
                    <span className="text-sm">
                      <strong className="text-red-600">*</strong> Pekerja yang didaftarkan harus sudah mengikuti safety briefing dan dalam kondisi sehat
                    </span>
                  </label>
                  {validationErrors.emv_disclaimer && (
                    <p className="text-xs text-red-600 mt-1">⚠️ {validationErrors.emv_disclaimer}</p>
                  )}
                </div>

                {/* Section 2: Hazard Identification */}
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">2. Identifikasi Bahaya</h4>
                  <p className="text-xs text-gray-600 mb-3">Dilakukan bersama pemberi dan penerima izin di lokasi</p>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { field: 'hazard_live_mv_busbar', label: 'Live MV busbar/konduktor' },
                      { field: 'hazard_live_incoming_cable', label: 'Live incoming MV cable' },
                      { field: 'hazard_charged_transformer', label: 'Trafo body bermuatan' },
                      { field: 'hazard_rain_outdoor', label: 'Kondisi luar hujan' },
                      { field: 'hazard_wet_floor', label: 'Lantai basah' },
                      { field: 'hazard_falling_objects', label: 'Tertimpa barang' },
                      { field: 'hazard_explosion', label: 'Ledakan' },
                      { field: 'hazard_confined_area', label: 'Area kerja terbatas' }
                    ].map((item) => (
                      <label key={item.field} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.high_risk_forms.electric_medium_voltage[item.field]}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              high_risk_forms: {
                                ...formData.high_risk_forms,
                                electric_medium_voltage: {
                                  ...formData.high_risk_forms.electric_medium_voltage,
                                  [item.field]: e.target.checked
                                }
                              }
                            });
                          }}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">{item.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Section 4: MV Checklist */}
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">4. ELECTRICAL MEDIUM VOLTAGE CHECKLIST</h4>
                  <div className="space-y-3">
                    {[
                      { field: 'checklist_outgoing_switch_off', label: 'Seluruh panel Outgoing Disconnecting Switch dalam kondisi off?' },
                      { field: 'checklist_outgoing_grounded', label: 'Seluruh panel Outgoing beban di-grounding?' },
                      { field: 'checklist_incoming_switch_off', label: 'Panel Incoming Disconnecting Switch dalam kondisi off?' },
                      { field: 'checklist_incoming_busbar_grounded', label: 'Panel Incoming dan busbar di-grounding?' },
                      { field: 'checklist_voltmeter_off', label: 'Volt meter dan lampu indicator off?' },
                      { field: 'checklist_incoming_breaker_loto', label: 'Breaker Incoming ditarik keluar bus duct atau sudah di padlock LOTO?' },
                      { field: 'checklist_outgoing_breaker_loto', label: 'Semua Breaker Outgoing ditarik keluar bus duct atau sudah di padlock LOTO?' },
                      { field: 'checklist_manual_clamp_grounding', label: 'Sudah ditambahkan clamp grounding manual dari bus bar ke grounding point?' },
                      { field: 'checklist_incoming_cable_off', label: 'Sumber listrik kabel Incoming/Panel PLN/Listrik Swasta dalam kondisi off?' },
                      { field: 'checklist_transformer_ground_ok', label: 'Grounding Trafo visual check kondisi baik?' }
                    ].map((item) => (
                      <div key={item.field}>
                        <label className="block text-sm font-medium mb-1">{item.label} <span className="text-red-500">*</span></label>
                        <select
                          value={formData.high_risk_forms.electric_medium_voltage[item.field]}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              high_risk_forms: {
                                ...formData.high_risk_forms,
                                electric_medium_voltage: {
                                  ...formData.high_risk_forms.electric_medium_voltage,
                                  [item.field]: e.target.value
                                }
                              }
                            });
                          }}
                          className={`w-full p-2 border rounded ${validationErrors[`emv_${item.field}`] ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                        >
                          <option value="">Pilih</option>
                          <option value="Ya">Ya</option>
                          <option value="Tidak">Tidak</option>
                        </select>
                        {validationErrors[`emv_${item.field}`] && (
                          <p className="text-red-500 text-xs mt-1">{validationErrors[`emv_${item.field}`]}</p>
                        )}
                      </div>
                    ))}
                    <div>
                      <label className="block text-sm font-medium mb-1">Di mana titik grounding Outgoing? <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={formData.high_risk_forms.electric_medium_voltage.checklist_outgoing_ground_point}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            high_risk_forms: {
                              ...formData.high_risk_forms,
                              electric_medium_voltage: {
                                ...formData.high_risk_forms.electric_medium_voltage,
                                checklist_outgoing_ground_point: e.target.value
                              }
                            }
                          });
                        }}
                        className={`w-full p-2 border rounded ${validationErrors.emv_checklist_outgoing_ground_point ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                        placeholder="Sebutkan titik grounding"
                      />
                      {validationErrors.emv_checklist_outgoing_ground_point && (
                        <p className="text-red-500 text-xs mt-1">{validationErrors.emv_checklist_outgoing_ground_point}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Di mana titik grounding Incoming? <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={formData.high_risk_forms.electric_medium_voltage.checklist_incoming_ground_point}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            high_risk_forms: {
                              ...formData.high_risk_forms,
                              electric_medium_voltage: {
                                ...formData.high_risk_forms.electric_medium_voltage,
                                checklist_incoming_ground_point: e.target.value
                              }
                            }
                          });
                        }}
                        className={`w-full p-2 border rounded ${validationErrors.emv_checklist_incoming_ground_point ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                        placeholder="Sebutkan titik grounding"
                      />
                      {validationErrors.emv_checklist_incoming_ground_point && (
                        <p className="text-red-500 text-xs mt-1">{validationErrors.emv_checklist_incoming_ground_point}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Section 5: Isolation */}
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">5. ISOLASI DAN ELIMINASI ENERGI</h4>
                  <div className="space-y-3">
                    {[
                      { field: 'isolation_uniform_safety_shoes', label: 'Menggunakan seragam kerja lengkap dengan safety shoes?' },
                      { field: 'isolation_electric_gloves', label: 'Menggunakan sarung tangan elektrik untuk bekerja di bus bar atau pengukuran pada konduktor trafo?' },
                      { field: 'isolation_safety_glasses', label: 'Menggunakan kacamata safety saat bekerja dengan oli trafo, gerinda, punching, drilling, cutting?' },
                      { field: 'isolation_tools_certified', label: 'Semua alat yang digunakan dalam kondisi baik dan telah lolos uji tools dengan izin yang masih berlaku?' },
                      { field: 'isolation_work_at_height_permit', label: 'Bekerja di ketinggian harus menyertakan Work at Height Permit?' },
                      { field: 'isolation_fire_extinguisher_abc', label: 'Tersedia APAR tipe ABC di lokasi?' }
                    ].map((item) => (
                      <div key={item.field}>
                        <label className="block text-sm font-medium mb-1">{item.label} <span className="text-red-500">*</span></label>
                        <select
                          value={formData.high_risk_forms.electric_medium_voltage[item.field]}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              high_risk_forms: {
                                ...formData.high_risk_forms,
                                electric_medium_voltage: {
                                  ...formData.high_risk_forms.electric_medium_voltage,
                                  [item.field]: e.target.value
                                }
                              }
                            });
                          }}
                          className={`w-full p-2 border rounded ${validationErrors[`emv_${item.field}`] ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                        >
                          <option value="">Pilih</option>
                          <option value="Ya">Ya</option>
                          <option value="Tidak">Tidak</option>
                        </select>
                        {validationErrors[`emv_${item.field}`] && (
                          <p className="text-red-500 text-xs mt-1">{validationErrors[`emv_${item.field}`]}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* CONFINED SPACE FORM */}
        {formData.high_risk_activities.includes('Confined Space') && (
          <div className="border border-green-300 rounded-lg p-6 bg-green-50">
            <h3 className="text-lg font-bold text-green-900 mb-4">🔒 PERMIT TO WORK CONFINED SPACE</h3>
            <div className="bg-red-100 border border-red-300 p-3 rounded mb-4">
              <p className="text-sm text-red-800 font-medium">
                1. ⚠️ PENTING: Jika dalam kondisi EMERGENCY, harap menghubungi PETUGAS SHE atau PIC LAPANGAN
              </p>
            </div>
            
            {!formData.high_risk_forms.confined_space && (
              <button
                type="button"
                onClick={() => initializeHighRiskForm('confined_space')}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
              >
                📝 Mulai Isi Form Confined Space
              </button>
            )}

            {formData.high_risk_forms.confined_space && (
              <div className="space-y-6">
                {/* Section 1: Space Type */}
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">2. Jenis/Deskripsi dari Confined Space</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { field: 'type_storage_vessels', label: 'Storage Vessels' },
                      { field: 'type_underground_space', label: 'Ruang dibawah Plafon' },
                      { field: 'type_shortcut_ladder', label: 'Tangga Shortcut' },
                      { field: 'type_silo_drum_mixer', label: 'Silo/Drum/Mixer' },
                      { field: 'type_electrical_room', label: 'Ruang Electrical' },
                      { field: 'type_under_roof_space', label: 'Ruang dibawah Atap' },
                      { field: 'type_bins_special_room', label: 'Bins/Ruang Khusus' }
                    ].map((item) => (
                      <label key={item.field} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.high_risk_forms.confined_space[item.field]}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              high_risk_forms: {
                                ...formData.high_risk_forms,
                                confined_space: {
                                  ...formData.high_risk_forms.confined_space,
                                  [item.field]: e.target.checked
                                }
                              }
                            });
                          }}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">{item.label}</span>
                      </label>
                    ))}
                  </div>
                  <div className="mt-3">
                    <label className="block text-sm font-medium mb-1">Lainnya (specify):</label>
                    <input
                      type="text"
                      value={formData.high_risk_forms.confined_space.type_other}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          high_risk_forms: {
                            ...formData.high_risk_forms,
                            confined_space: {
                              ...formData.high_risk_forms.confined_space,
                              type_other: e.target.value
                            }
                          }
                        });
                      }}
                      className="w-full p-2 border border-gray-300 rounded"
                      placeholder="Sebutkan jenis lainnya"
                    />
                  </div>
                </div>

                {/* Section 2: Work Type */}
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">3. Jenis Pekerjaan yang akan dilakukan</h4>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.high_risk_forms.confined_space.work_inspection}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            high_risk_forms: {
                              ...formData.high_risk_forms,
                              confined_space: {
                                ...formData.high_risk_forms.confined_space,
                                work_inspection: e.target.checked
                              }
                            }
                          });
                        }}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Inspeksi didalam Confined Space</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.high_risk_forms.confined_space.work_inside_confined}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            high_risk_forms: {
                              ...formData.high_risk_forms,
                              confined_space: {
                                ...formData.high_risk_forms.confined_space,
                                work_inside_confined: e.target.checked
                              }
                            }
                          });
                        }}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Bekerja didalam Confined Space</span>
                    </label>
                  </div>
                </div>

                {/* Section 3: Materials/Chemicals */}
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">4. Jenis Material atau Bahan Kimia</h4>
                  <p className="text-xs text-gray-600 mb-2">SDS dari Bahan material atau kimia tersebut harus dilampirkan</p>
                  <textarea
                    value={formData.high_risk_forms.confined_space.materials_chemicals}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        high_risk_forms: {
                          ...formData.high_risk_forms,
                          confined_space: {
                            ...formData.high_risk_forms.confined_space,
                            materials_chemicals: e.target.value
                          }
                        }
                      });
                    }}
                    className="w-full p-2 border border-gray-300 rounded"
                    rows="2"
                    placeholder="Sebutkan material/bahan kimia yang ada di lokasi atau yang dibawa"
                  />
                </div>

                {/* Section 5: Hazards */}
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">5. PERIKSA SEMUA HAZARDS YANG BERPOTENSI</h4>
                  
                  <div className="mb-4">
                    <h5 className="font-medium text-gray-700 mb-2">5.1 Hazardous Atmospheric</h5>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { field: 'hazard_atm_flammable', label: 'Flammable' },
                        { field: 'hazard_atm_toxic', label: 'Toxic' },
                        { field: 'hazard_atm_irritant', label: 'Irritant' },
                        { field: 'hazard_atm_corrosive', label: 'Corrosive' },
                        { field: 'hazard_atm_oxygen_deficient', label: 'Oxygen Deficient' },
                        { field: 'hazard_atm_oxygen_enriched', label: 'Oxygen Enriched' }
                      ].map((item) => (
                        <label key={item.field} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={formData.high_risk_forms.confined_space[item.field]}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                high_risk_forms: {
                                  ...formData.high_risk_forms,
                                  confined_space: {
                                    ...formData.high_risk_forms.confined_space,
                                    [item.field]: e.target.checked
                                  }
                                }
                              });
                            }}
                            className="w-4 h-4"
                          />
                          <span className="text-sm">{item.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h5 className="font-medium text-gray-700 mb-2">5.2 Physical Hazards</h5>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { field: 'hazard_phy_temperature', label: 'Temperature' },
                        { field: 'hazard_phy_chemical_absorption', label: 'Chemical Absorption' },
                        { field: 'hazard_phy_noise', label: 'Noise' },
                        { field: 'hazard_phy_entrapment', label: 'Entrapment' },
                        { field: 'hazard_phy_vibration', label: 'Vibration' },
                        { field: 'hazard_phy_electrical_equipment', label: 'Electrical Equipment' },
                        { field: 'hazard_phy_mechanical_equipment', label: 'Mechanical Equipment' },
                        { field: 'hazard_phy_spark_operations', label: 'Spark-producing Operations' },
                        { field: 'hazard_phy_spilled_liquids', label: 'Spilled Liquids' },
                        { field: 'hazard_phy_engulfment', label: 'Engulfment' },
                        { field: 'hazard_phy_radiation', label: 'Radiation' },
                        { field: 'hazard_phy_entry_exit_limitation', label: 'Entry dan Exit Limitation' }
                      ].map((item) => (
                        <label key={item.field} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={formData.high_risk_forms.confined_space[item.field]}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                high_risk_forms: {
                                  ...formData.high_risk_forms,
                                  confined_space: {
                                    ...formData.high_risk_forms.confined_space,
                                    [item.field]: e.target.checked
                                  }
                                }
                              });
                            }}
                            className="w-4 h-4"
                          />
                          <span className="text-sm">{item.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Section 6: PPE - Simplified for brevity but with structure */}
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">6. PERSONAL PROTECTIVE EQUIPMENT</h4>
                  <p className="text-xs text-gray-600 mb-3">Pilih PPE yang akan dipakai</p>
                  
                  <div className="space-y-4">
                    <div>
                      <h5 className="font-medium text-gray-700 mb-2">6.1 Respiratory Protection</h5>
                      <div className="grid grid-cols-2 gap-2">
                        {['ppe_resp_scba', 'ppe_resp_air_purifying', 'ppe_resp_air_line'].map((field) => (
                          <label key={field} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.high_risk_forms.confined_space[field]}
                              onChange={(e) => {
                                setFormData({
                                  ...formData,
                                  high_risk_forms: {
                                    ...formData.high_risk_forms,
                                    confined_space: {
                                      ...formData.high_risk_forms.confined_space,
                                      [field]: e.target.checked
                                    }
                                  }
                                });
                              }}
                              className="w-4 h-4"
                            />
                            <span className="text-sm">{field.replace('ppe_resp_', '').replace(/_/g, ' ').toUpperCase()}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h5 className="font-medium text-gray-700 mb-2">6.2 Eye Protection</h5>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { field: 'ppe_eye_safety_glasses', label: 'Safety Glasses' },
                          { field: 'ppe_eye_goggles', label: 'Goggles' }
                        ].map((item) => (
                          <label key={item.field} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.high_risk_forms.confined_space[item.field]}
                              onChange={(e) => {
                                setFormData({
                                  ...formData,
                                  high_risk_forms: {
                                    ...formData.high_risk_forms,
                                    confined_space: {
                                      ...formData.high_risk_forms.confined_space,
                                      [item.field]: e.target.checked
                                    }
                                  }
                                });
                              }}
                              className="w-4 h-4"
                            />
                            <span className="text-sm">{item.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h5 className="font-medium text-gray-700 mb-2">6.3 Routine Protection</h5>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { field: 'ppe_routine_gloves', label: 'Gloves' },
                          { field: 'ppe_routine_chemical_suit', label: 'Chemical Suit' },
                          { field: 'ppe_routine_hard_hat', label: 'Hard hat' },
                          { field: 'ppe_routine_overall', label: 'Overall' },
                          { field: 'ppe_routine_earplug', label: 'Earplug/muffs' },
                          { field: 'ppe_routine_safety_boots', label: 'Safety Boots' }
                        ].map((item) => (
                          <label key={item.field} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.high_risk_forms.confined_space[item.field]}
                              onChange={(e) => {
                                setFormData({
                                  ...formData,
                                  high_risk_forms: {
                                    ...formData.high_risk_forms,
                                    confined_space: {
                                      ...formData.high_risk_forms.confined_space,
                                      [item.field]: e.target.checked
                                    }
                                  }
                                });
                              }}
                              className="w-4 h-4"
                            />
                            <span className="text-sm">{item.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h5 className="font-medium text-gray-700 mb-2">6.4 Rescue or Access Equipment</h5>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { field: 'ppe_rescue_escape_set', label: 'Escape Set' },
                          { field: 'ppe_rescue_communication', label: 'Communication (2-Way Radio)' },
                          { field: 'ppe_rescue_motion_detector', label: 'Motion Detector' },
                          { field: 'ppe_rescue_life_line', label: 'Life Line' },
                          { field: 'ppe_rescue_lifting_gear', label: 'Lifting Gear' }
                        ].map((item) => (
                          <label key={item.field} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.high_risk_forms.confined_space[item.field]}
                              onChange={(e) => {
                                setFormData({
                                  ...formData,
                                  high_risk_forms: {
                                    ...formData.high_risk_forms,
                                    confined_space: {
                                      ...formData.high_risk_forms.confined_space,
                                      [item.field]: e.target.checked
                                    }
                                  }
                                });
                              }}
                              className="w-4 h-4"
                            />
                            <span className="text-sm">{item.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h5 className="font-medium text-gray-700 mb-2">6.5 Essential Requirement</h5>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { field: 'ppe_essential_fire_extinguisher', label: 'Fire Extinguisher' },
                          { field: 'ppe_essential_first_aid', label: 'First Aid Kits' },
                          { field: 'ppe_essential_torch', label: 'Torch' }
                        ].map((item) => (
                          <label key={item.field} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.high_risk_forms.confined_space[item.field]}
                              onChange={(e) => {
                                setFormData({
                                  ...formData,
                                  high_risk_forms: {
                                    ...formData.high_risk_forms,
                                    confined_space: {
                                      ...formData.high_risk_forms.confined_space,
                                      [item.field]: e.target.checked
                                    }
                                  }
                                });
                              }}
                              className="w-4 h-4"
                            />
                            <span className="text-sm">{item.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 7: Hot Work Questions */}
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">7. PEKERJAAN HOT WORK? (Diisi Bersama dengan Pengawas Pemberi Kerja)</h4>
                  <div className="space-y-3">
                    {[
                      { field: 'hot_work_area_clear_15m', label: 'Apakah area disekitar confined space bebas sampai > 15 m, jika terjadi kebakaran?' },
                      { field: 'hot_work_fire_extinguisher_available', label: 'Apakah Fire Extinguisher yang cocok sudah ada dilapangan?' },
                      { field: 'hot_work_air_free_gas', label: 'Apakah udara sekitar confined space bebas dari gas-gas lainnya?' },
                      { field: 'hot_work_entry_exit_safe', label: 'Apakah Akses masuk (Entry) dan Keluar (Exit) benar-benar aman?' }
                    ].map((item) => (
                      <div key={item.field}>
                        <label className="block text-sm font-medium mb-1">{item.label}</label>
                        <select
                          value={formData.high_risk_forms.confined_space[item.field] || ''}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              high_risk_forms: {
                                ...formData.high_risk_forms,
                                confined_space: {
                                  ...formData.high_risk_forms.confined_space,
                                  [item.field]: e.target.value
                                }
                              }
                            });
                          }}
                          className="w-full p-2 border border-gray-300 rounded text-sm"
                        >
                          <option value="">Pilih</option>
                          <option value="Ya">Ya</option>
                          <option value="Tidak">Tidak</option>
                        </select>
                      </div>
                    ))}
                    <div>
                      <label className="block text-sm font-medium mb-1">Apakah Pekerjaan Hot Work atau Pengelasan ada? <span className="text-red-500">*</span></label>
                      <select
                        value={formData.high_risk_forms.confined_space.hot_work_welding_exists || ''}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            high_risk_forms: {
                              ...formData.high_risk_forms,
                              confined_space: {
                                ...formData.high_risk_forms.confined_space,
                                hot_work_welding_exists: e.target.value
                              }
                            }
                          });
                        }}
                        className={`w-full p-2 border rounded ${validationErrors.cs_hot_work_welding_exists ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                      >
                        <option value="">Pilih</option>
                        <option value="Ya">Ya</option>
                        <option value="Tidak">Tidak</option>
                      </select>
                      {validationErrors.cs_hot_work_welding_exists && (
                        <p className="text-red-500 text-xs mt-1">{validationErrors.cs_hot_work_welding_exists}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm flex-1">Tipe dari Extinguisher yang dipakai/tersedia:</span>
                      <input
                        type="text"
                        value={formData.high_risk_forms.confined_space.hot_work_extinguisher_type || ''}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            high_risk_forms: {
                              ...formData.high_risk_forms,
                              confined_space: {
                                ...formData.high_risk_forms.confined_space,
                                hot_work_extinguisher_type: e.target.value
                              }
                            }
                          });
                        }}
                        className="w-48 p-2 border border-gray-300 rounded text-sm"
                        placeholder="Tipe extinguisher"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">Jika pekerjaan Hot Work ada (YA), lanjutkan dengan Checklist BAGIAN 8!</p>
                </div>

                {/* Section 8: Hot Work Inspection - conditional */}
                {formData.high_risk_forms.confined_space.hot_work_welding_exists === 'Ya' && (
                  <div className="bg-white p-4 rounded-lg border-2 border-orange-300">
                    <h4 className="font-semibold text-orange-800 mb-3">8. INSPEKSI PEKERJAAN HOTWORK DIDALAM CONFINED SPACE</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium mb-1">Apakah personal yang melakukan pekerjaan hotwork sudah memiliki Sertifikat dari ASME Section IV atau IX? <span className="text-red-500">*</span></label>
                        <select
                          value={formData.high_risk_forms.confined_space.hot_work_cert_asme || ''}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              high_risk_forms: {
                                ...formData.high_risk_forms,
                                confined_space: {
                                  ...formData.high_risk_forms.confined_space,
                                  hot_work_cert_asme: e.target.value
                                }
                              }
                            });
                          }}
                          className={`w-full p-2 border rounded ${validationErrors.cs_hot_work_cert ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                        >
                          <option value="">Pilih</option>
                          <option value="Ya">Ya</option>
                          <option value="Tidak">Tidak</option>
                        </select>
                        {validationErrors.cs_hot_work_cert && (
                          <p className="text-red-500 text-xs mt-1">{validationErrors.cs_hot_work_cert}</p>
                        )}
                      </div>
                      {formData.high_risk_forms.confined_space.hot_work_cert_asme === 'Tidak' && (
                        <div className="ml-4">
                          <label className="text-sm">Jika TIDAK, sebutkan sertifikat yang dimiliki:</label>
                          <input
                            type="text"
                            value={formData.high_risk_forms.confined_space.hot_work_cert_other || ''}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                high_risk_forms: {
                                  ...formData.high_risk_forms,
                                  confined_space: {
                                    ...formData.high_risk_forms.confined_space,
                                    hot_work_cert_other: e.target.value
                                  }
                                }
                              });
                            }}
                            className="w-full p-2 border border-gray-300 rounded text-sm mt-1"
                            placeholder="Nama sertifikat"
                          />
                        </div>
                      )}
                      <div>
                        <label className="text-sm font-medium">List Peralatan Hotwork yang akan dipakai: <span className="text-red-500">*</span></label>
                        <textarea
                          value={formData.high_risk_forms.confined_space.hot_work_equipment_list || ''}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              high_risk_forms: {
                                ...formData.high_risk_forms,
                                confined_space: {
                                  ...formData.high_risk_forms.confined_space,
                                  hot_work_equipment_list: e.target.value
                                }
                              }
                            });
                          }}
                          className={`w-full p-2 border rounded text-sm mt-1 ${validationErrors.cs_hot_work_equipment ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                          rows="2"
                          placeholder="List peralatan"
                        />
                        {validationErrors.cs_hot_work_equipment && (
                          <p className="text-red-500 text-xs mt-1">{validationErrors.cs_hot_work_equipment}</p>
                        )}
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">Jenis Hot work yang akan dipakai:</label>
                        <div className="flex gap-4">
                          {[
                            { field: 'hot_work_type_acetylene', label: 'Acetylene Weld' },
                            { field: 'hot_work_type_arc_welding', label: 'Arc Welding' },
                            { field: 'hot_work_type_oxygen_cutting', label: 'Oxygen Cutting/Welding' }
                          ].map((item) => (
                            <label key={item.field} className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={formData.high_risk_forms.confined_space[item.field]}
                                onChange={(e) => {
                                  setFormData({
                                    ...formData,
                                    high_risk_forms: {
                                      ...formData.high_risk_forms,
                                      confined_space: {
                                        ...formData.high_risk_forms.confined_space,
                                        [item.field]: e.target.checked
                                      }
                                    }
                                  });
                                }}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">{item.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm">Apakah tabung Oxygen/Acetylene memiliki sertifikat? <span className="text-red-500">*</span></label>
                          <select
                            value={formData.high_risk_forms.confined_space.hot_work_cylinder_certified || ''}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                high_risk_forms: {
                                  ...formData.high_risk_forms,
                                  confined_space: {
                                    ...formData.high_risk_forms.confined_space,
                                    hot_work_cylinder_certified: e.target.value
                                  }
                                }
                              });
                            }}
                            className={`w-full p-2 border rounded text-sm mt-1 ${validationErrors.cs_hot_work_cylinder ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                          >
                            <option value="">Pilih</option>
                            <option value="Ya">Ya</option>
                            <option value="Tidak">Tidak</option>
                          </select>
                          {validationErrors.cs_hot_work_cylinder && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors.cs_hot_work_cylinder}</p>
                          )}
                        </div>
                        <div>
                          <label className="text-sm">Kondisi hose Oxygen dan Acetylene: <span className="text-red-500">*</span></label>
                          <select
                            value={formData.high_risk_forms.confined_space.hot_work_hose_condition || ''}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                high_risk_forms: {
                                  ...formData.high_risk_forms,
                                  confined_space: {
                                    ...formData.high_risk_forms.confined_space,
                                    hot_work_hose_condition: e.target.value
                                  }
                                }
                              });
                            }}
                            className={`w-full p-2 border rounded text-sm mt-1 ${validationErrors.cs_hot_work_hose ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                          >
                            <option value="">Pilih</option>
                            <option value="Baik">Baik</option>
                            <option value="Rusak">Rusak</option>
                            <option value="Perlu perbaikan">Perlu perbaikan</option>
                          </select>
                          {validationErrors.cs_hot_work_hose && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors.cs_hot_work_hose}</p>
                          )}
                        </div>
                        <div>
                          <label className="text-sm">Kondisi sambungan/Kopling hoses: <span className="text-red-500">*</span></label>
                          <select
                            value={formData.high_risk_forms.confined_space.hot_work_coupling_condition || ''}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                high_risk_forms: {
                                  ...formData.high_risk_forms,
                                  confined_space: {
                                    ...formData.high_risk_forms.confined_space,
                                    hot_work_coupling_condition: e.target.value
                                  }
                                }
                              });
                            }}
                            className={`w-full p-2 border rounded text-sm mt-1 ${validationErrors.cs_hot_work_coupling ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                          >
                            <option value="">Pilih</option>
                            <option value="Baik">Baik</option>
                            <option value="Rusak">Rusak</option>
                            <option value="Perlu perbaikan">Perlu perbaikan</option>
                          </select>
                          {validationErrors.cs_hot_work_coupling && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors.cs_hot_work_coupling}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Section 10: Hazard Controls */}
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">10. HAZARD CONTROLS (Diisi Bersama oleh pengawas pemberi Kerja)</h4>
                  <div className="space-y-3">
                    {[
                      { field: 'control_loto_required', label: 'Apakah Prosedur Lockout/tagout (LOTO) diperlukan?' },
                      { field: 'control_gfci_required', label: 'Apakah GFCI (Ground Fault Circuit Interupter) diperlukan untuk semua peralatan Listrik?' },
                      { field: 'control_explosion_proof_required', label: 'Apakah Peralatan explosion-proof diperlukan?' },
                      { field: 'control_barrier_required', label: 'Apakah barrier/penghalang diperlukan?' },
                      { field: 'control_communication_required', label: 'Apakah Peralatan Komunikasi diperlukan?' },
                      { field: 'control_msds_attached', label: 'Apakah SDS Material yang didalam confined space dilampirkan?' },
                      { field: 'control_workers_trained', label: 'Apakah orang yang akan memasuki confined space telah di training?' },
                      { field: 'control_standby_notified', label: 'Apakah standby person telah diberitahu untuk tetap diposisinya?' },
                      { field: 'control_space_cleaned', label: 'Apakah sebelum memasuki confined space perlu dibersihkan?' },
                      { field: 'control_space_emptied', label: 'Apakah prosedur mengosongkan gas sudah dilaksanakan?' }
                    ].map((item) => (
                      <div key={item.field}>
                        <label className="block text-sm font-medium mb-1">{item.label}</label>
                        <select
                          value={formData.high_risk_forms.confined_space[item.field] || ''}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              high_risk_forms: {
                                ...formData.high_risk_forms,
                                confined_space: {
                                  ...formData.high_risk_forms.confined_space,
                                  [item.field]: e.target.value
                                }
                              }
                            });
                          }}
                          className="w-full p-2 border border-gray-300 rounded text-sm"
                        >
                          <option value="">Pilih</option>
                          <option value="Ya">Ya</option>
                          <option value="Tidak">Tidak</option>
                        </select>
                      </div>
                    ))}
                    {formData.high_risk_forms.confined_space.control_loto_required === 'Ya' && (
                      <div className="ml-4">
                        <label className="text-sm">Sebutkan titik-titik lokasi LOTO:</label>
                        <input
                          type="text"
                          value={formData.high_risk_forms.confined_space.control_loto_points || ''}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              high_risk_forms: {
                                ...formData.high_risk_forms,
                                confined_space: {
                                  ...formData.high_risk_forms.confined_space,
                                  control_loto_points: e.target.value
                                }
                              }
                            });
                          }}
                          className="w-full p-2 border border-gray-300 rounded text-sm mt-1"
                          placeholder="Titik LOTO"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Section 10B: Hazard Controls Table */}
                <div className="bg-white p-4 rounded-lg mt-4">
                  <h4 className="font-semibold text-gray-800 mb-3">10B. HAZARD CONTROLS - Checklist Izin Kerja Ruang Terbatas</h4>
                  <p className="text-sm font-medium text-gray-700 mb-3">Checklist Izin Kerja Ruang Terbatas</p>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-300 text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="border border-gray-300 p-2 text-left">Pertanyaan</th>
                          <th className="border border-gray-300 p-2 text-center w-16">YA <span className="text-red-500">*</span></th>
                          <th className="border border-gray-300 p-2 text-center w-16">Tidak <span className="text-red-500">*</span></th>
                          <th className="border border-gray-300 p-2 text-center">Isolasi Sebelum Entry</th>
                          <th className="border border-gray-300 p-2 text-center">Isolasi dikembalikan setelah exit</th>
                          <th className="border border-gray-300 p-2 text-left">Efek Isolasi ini terhadap lokasi lain</th>
                        </tr>
                      </thead>
                      <tbody>
                        {hazardControlsTable.map((row) => {
                          const isDisabled = row.tidak === true;
                          const hasError = validationErrors[`cs_hazard_table_${row.id - 1}`];
                          return (
                            <tr key={row.id}>
                              <td className="border border-gray-300 p-2">
                                {row.pertanyaan}
                              </td>
                              <td className="border border-gray-300 p-2 text-center">
                                <input
                                  type="checkbox"
                                  checked={row.ya}
                                  onChange={(e) => updateHazardControlsTable(row.id, 'ya', e.target.checked)}
                                  className="w-4 h-4"
                                />
                              </td>
                              <td className="border border-gray-300 p-2 text-center">
                                <input
                                  type="checkbox"
                                  checked={row.tidak}
                                  onChange={(e) => updateHazardControlsTable(row.id, 'tidak', e.target.checked)}
                                  className="w-4 h-4"
                                />
                                {hasError && (
                                  <p className="text-red-500 text-xs mt-1">⚠️</p>
                                )}
                              </td>
                              <td className="border border-gray-300 p-2 text-center">
                                <input
                                  type="checkbox"
                                  checked={row.isolasi_sebelum}
                                  onChange={(e) => updateHazardControlsTable(row.id, 'isolasi_sebelum', e.target.checked)}
                                  disabled={isDisabled}
                                  className={`w-4 h-4 ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                />
                              </td>
                              <td className="border border-gray-300 p-2 text-center">
                                <input
                                  type="checkbox"
                                  checked={row.isolasi_setelah}
                                  onChange={(e) => updateHazardControlsTable(row.id, 'isolasi_setelah', e.target.checked)}
                                  disabled={isDisabled}
                                  className={`w-4 h-4 ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                />
                              </td>
                              <td className="border border-gray-300 p-2">
                                <input
                                  type="text"
                                  value={row.efek_ke_lokasi_lain}
                                  onChange={(e) => updateHazardControlsTable(row.id, 'efek_ke_lokasi_lain', e.target.value)}
                                  disabled={isDisabled}
                                  className={`w-full p-1 border border-gray-300 rounded text-sm ${isDisabled ? 'bg-gray-100' : ''}`}
                                  placeholder="Jelaskan efeknya..."
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {validationErrors.cs_hazard_table && (
                    <p className="text-red-500 text-xs mt-2">{validationErrors.cs_hazard_table}</p>
                  )}
                </div>

                {/* Section 11: Ventilasi */}
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">11. Ventilasi dan Saluran Udara</h4>
                  
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Apakah Ventilasi yang ada cukup untuk sumber oxygen? <span className="text-red-500">*</span>
                    </p>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="ventilasi_cukup"
                          value="Ya"
                          checked={ventilasiData.cukup === 'Ya'}
                          onChange={() => updateVentilasi('cukup', 'Ya')}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">Ya</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="ventilasi_cukup"
                          value="Tidak"
                          checked={ventilasiData.cukup === 'Tidak'}
                          onChange={() => updateVentilasi('cukup', 'Tidak')}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">Tidak</span>
                      </label>
                    </div>
                    {validationErrors.cs_ventilasi_cukup && (
                      <p className="text-red-500 text-xs mt-1">{validationErrors.cs_ventilasi_cukup}</p>
                    )}
                  </div>
                  
                  {ventilasiData.cukup === 'Tidak' && (
                    <div className="mt-4 pl-4 border-l-4 border-yellow-400">
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        Jenis Ventilasi yang di Usulkan adalah : <span className="text-red-500">*</span>
                      </p>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2">
                          <input
                            type="radio"
                            name="ventilasi_jenis"
                            value="Mechanical"
                            checked={ventilasiData.jenis === 'Mechanical'}
                            onChange={() => updateVentilasi('jenis', 'Mechanical')}
                            className="w-4 h-4"
                          />
                          <span className="text-sm">Mechanical</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="radio"
                            name="ventilasi_jenis"
                            value="Natural"
                            checked={ventilasiData.jenis === 'Natural'}
                            onChange={() => updateVentilasi('jenis', 'Natural')}
                            className="w-4 h-4"
                          />
                          <span className="text-sm">Natural</span>
                        </label>
                      </div>
                      {validationErrors.cs_ventilasi_jenis && (
                        <p className="text-red-500 text-xs mt-1">{validationErrors.cs_ventilasi_jenis}</p>
                      )}
                    </div>
                  )}                       
                </div>

                {/* Section 12: Permit Review */}
                {/* 🔴 STEP 8: Section 12 - Permit Review 🔴 */}
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">12. Permit Review</h4>
                  <div className="space-y-3">
                    
                    {/* Field 1: Monitoring Udara */}
                    <div>
                      <p className="text-sm font-medium text-gray-700">Apakah Monitoring udara sudah diidentifikasi dengan benar? <span className="text-red-500">*</span></p>
                      <div className={`flex gap-4 mt-1 p-2 rounded ${validationErrors.permit_review_monitoring ? 'bg-red-50 border border-red-500' : ''}`}>
                        <label className="flex items-center gap-2">
                          <input type="radio" name="monitoring_udara" value="Ya" checked={permitReview.monitoring_udara === 'Ya'} onChange={() => updatePermitReview('monitoring_udara', 'Ya')} className="w-4 h-4" />
                          <span>Ya</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input type="radio" name="monitoring_udara" value="Tidak" checked={permitReview.monitoring_udara === 'Tidak'} onChange={() => updatePermitReview('monitoring_udara', 'Tidak')} className="w-4 h-4" />
                          <span>Tidak</span>
                        </label>
                      </div>
                      {validationErrors.permit_review_monitoring && <p className="text-red-500 text-xs mt-1">{validationErrors.permit_review_monitoring}</p>}
                    </div>
                    
                    {/* Field 2: Respirator */}
                    <div>
                      <p className="text-sm font-medium text-gray-700">Apakah peralatan Respirator diperlukan? <span className="text-red-500">*</span></p>
                      <div className={`flex gap-4 mt-1 p-2 rounded ${validationErrors.permit_review_respirator ? 'bg-red-50 border border-red-500' : ''}`}>
                        <label className="flex items-center gap-2">
                          <input type="radio" name="respirator" value="Ya" checked={permitReview.respirator_diperlukan === 'Ya'} onChange={() => updatePermitReview('respirator_diperlukan', 'Ya')} className="w-4 h-4" />
                          <span>Ya</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input type="radio" name="respirator" value="Tidak" checked={permitReview.respirator_diperlukan === 'Tidak'} onChange={() => updatePermitReview('respirator_diperlukan', 'Tidak')} className="w-4 h-4" />
                          <span>Tidak</span>
                        </label>
                      </div>
                      {validationErrors.permit_review_respirator && <p className="text-red-500 text-xs mt-1">{validationErrors.permit_review_respirator}</p>}
                    </div>
                    
                    {/* Field 3: Hazard Controls & PPE */}
                    <div>
                      <p className="text-sm font-medium text-gray-700">Apakah Hazards Control dan PPE sudah tepat dipilih? <span className="text-red-500">*</span></p>
                      <div className={`flex gap-4 mt-1 p-2 rounded ${validationErrors.permit_review_hazard ? 'bg-red-50 border border-red-500' : ''}`}>
                        <label className="flex items-center gap-2">
                          <input type="radio" name="hazard_controls" value="Ya" checked={permitReview.hazard_controls_ppe === 'Ya'} onChange={() => updatePermitReview('hazard_controls_ppe', 'Ya')} className="w-4 h-4" />
                          <span>Ya</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input type="radio" name="hazard_controls" value="Tidak" checked={permitReview.hazard_controls_ppe === 'Tidak'} onChange={() => updatePermitReview('hazard_controls_ppe', 'Tidak')} className="w-4 h-4" />
                          <span>Tidak</span>
                        </label>
                      </div>
                      {validationErrors.permit_review_hazard && <p className="text-red-500 text-xs mt-1">{validationErrors.permit_review_hazard}</p>}
                    </div>
                    
                    {/* Field 4: Plant Engineer & Safety Officer */}
                    <div>
                      <p className="text-sm font-medium text-gray-700">Apakah Plant Engineer dan Safety Officer sudah menyetujui Prosedur Operasi ini? <span className="text-red-500">*</span></p>
                      <div className={`flex gap-4 mt-1 p-2 rounded ${validationErrors.permit_review_approval ? 'bg-red-50 border border-red-500' : ''}`}>
                        <label className="flex items-center gap-2">
                          <input type="radio" name="plant_safety" value="Ya" checked={permitReview.plant_safety_approval === 'Ya'} onChange={() => updatePermitReview('plant_safety_approval', 'Ya')} className="w-4 h-4" />
                          <span>Ya</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input type="radio" name="plant_safety" value="Tidak" checked={permitReview.plant_safety_approval === 'Tidak'} onChange={() => updatePermitReview('plant_safety_approval', 'Tidak')} className="w-4 h-4" />
                          <span>Tidak</span>
                        </label>
                      </div>
                      {validationErrors.permit_review_approval && <p className="text-red-500 text-xs mt-1">{validationErrors.permit_review_approval}</p>}
                    </div>
                    
                    {/* Field 5: Monitoring Berkelanjutan */}
                    <div>
                      <p className="text-sm font-medium text-gray-700">Apakah Monitoring udara secara terus menerus diperlukan? <span className="text-red-500">*</span></p>
                      <div className={`flex gap-4 mt-1 p-2 rounded ${validationErrors.permit_review_monitoring_lanjut ? 'bg-red-50 border border-red-500' : ''}`}>
                        <label className="flex items-center gap-2">
                          <input type="radio" name="monitoring_lanjut" value="Ya" checked={permitReview.monitoring_berkelanjutan === 'Ya'} onChange={() => updatePermitReview('monitoring_berkelanjutan', 'Ya')} className="w-4 h-4" />
                          <span>Ya</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input type="radio" name="monitoring_lanjut" value="Tidak" checked={permitReview.monitoring_berkelanjutan === 'Tidak'} onChange={() => updatePermitReview('monitoring_berkelanjutan', 'Tidak')} className="w-4 h-4" />
                          <span>Tidak</span>
                        </label>
                      </div>
                      {validationErrors.permit_review_monitoring_lanjut && <p className="text-red-500 text-xs mt-1">{validationErrors.permit_review_monitoring_lanjut}</p>}
                    </div>
                    
                  </div>
                </div>

                {/* Section 13: Hasil Test/Pengukuran Kualitas Udara */}
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">13. Hasil Test/Pengukuran Kualitas Udara</h4>
                  
                  <p className="text-sm font-medium text-gray-700 mb-2">Peralatan Yang Digunakan</p>
                  <div className="overflow-x-auto mb-4">
                    <table className="w-full border-collapse border border-gray-300 text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="border p-2">Model</th>
                          <th className="border p-2">Serial Number</th>
                          <th className="border p-2">Tanggal Kalibrasi</th>
                          <th className="border p-2">Pemilik Alat/PT</th>
                          <th className="border p-2 w-16">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {peralatanTable.map(row => (
                          <tr key={row.id}>
                            <td className="border p-1"><input type="text" value={row.model} onChange={(e) => updatePeralatanRow(row.id, 'model', e.target.value)} className="w-full p-1 border rounded text-xs" /></td>
                            <td className="border p-1"><input type="text" value={row.serial_number} onChange={(e) => updatePeralatanRow(row.id, 'serial_number', e.target.value)} className="w-full p-1 border rounded text-xs" /></td>
                            <td className="border p-1"><input type="date" value={row.tanggal_kalibrasi} onChange={(e) => updatePeralatanRow(row.id, 'tanggal_kalibrasi', e.target.value)} className="w-full p-1 border rounded text-xs" /></td>
                            <td className="border p-1"><input type="text" value={row.pemilik} onChange={(e) => updatePeralatanRow(row.id, 'pemilik', e.target.value)} className="w-full p-1 border rounded text-xs" /></td>
                            <td className="border p-1 text-center"><button onClick={() => deletePeralatanRow(row.id)} className="text-red-500"><Trash2 size={16} /></button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <button onClick={addPeralatanRow} className="mt-2 text-blue-600 text-sm flex items-center gap-1"><Plus size={14} /> Tambah Baris</button>
                  </div>
                  
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700">Apakah Monitoring semua udara yang diperlukan sudah dilakukan sepanjang confined spaces? <span className="text-red-500">*</span></p>
                    <div className="flex gap-4 mt-1">
                      <label className="flex items-center gap-2"><input type="radio" name="monitoring_udara_terus" value="Ya" checked={monitoringUdaraTerusMenerus === 'Ya'} onChange={() => { setMonitoringUdaraTerusMenerus('Ya'); setFormData(prev => ({ ...prev, high_risk_forms: { ...prev.high_risk_forms, confined_space: { ...prev.high_risk_forms.confined_space, monitoring_udara_terus_menerus: 'Ya' } } })); }} className="w-4 h-4" /><span>Ya</span></label>
                      <label className="flex items-center gap-2"><input type="radio" name="monitoring_udara_terus" value="Tidak" checked={monitoringUdaraTerusMenerus === 'Tidak'} onChange={() => { setMonitoringUdaraTerusMenerus('Tidak'); setFormData(prev => ({ ...prev, high_risk_forms: { ...prev.high_risk_forms, confined_space: { ...prev.high_risk_forms.confined_space, monitoring_udara_terus_menerus: 'Tidak' } } })); }} className="w-4 h-4" /><span>Tidak</span></label>
                    </div>
                    {validationErrors.monitoring_udara_terus_menerus && <p className="text-red-500 text-xs mt-1">{validationErrors.monitoring_udara_terus_menerus}</p>}
                  </div>
                  
                  <p className="text-sm font-medium text-gray-700 mb-2">Pengukuran Udara secara reguler selama pekerjaan di confined space</p>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-300 text-xs">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="border p-1">Tanggal</th><th className="border p-1">Waktu</th><th className="border p-1">Isolasi</th>
                          <th className="border p-1">O2 %</th><th className="border p-1">Combustible</th><th className="border p-1">% LEL</th>
                          <th className="border p-1">H2S</th><th className="border p-1">CO</th><th className="border p-1">Toxic/Chemical</th><th className="border p-1 w-12">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pengukuranUdaraTable.map(row => (
                          <tr key={row.id}>
                            <td className="border p-1"><input type="date" value={row.tanggal} onChange={(e) => updatePengukuranUdaraRow(row.id, 'tanggal', e.target.value)} className="w-full p-1 border rounded" /></td>
                            <td className="border p-1"><input type="time" value={row.waktu} onChange={(e) => updatePengukuranUdaraRow(row.id, 'waktu', e.target.value)} className="w-full p-1 border rounded" /></td>
                            <td className="border p-1"><input type="text" value={row.isolasi} onChange={(e) => updatePengukuranUdaraRow(row.id, 'isolasi', e.target.value)} className="w-full p-1 border rounded" placeholder="Isolasi" /></td>
                            <td className="border p-1"><input type="text" value={row.oksigen} onChange={(e) => updatePengukuranUdaraRow(row.id, 'oksigen', e.target.value)} className="w-full p-1 border rounded" placeholder="19.5-23.5%" /></td>
                            <td className="border p-1"><input type="text" value={row.combustible} onChange={(e) => updatePengukuranUdaraRow(row.id, 'combustible', e.target.value)} className="w-full p-1 border rounded" /></td>
                            <td className="border p-1"><input type="text" value={row.lel} onChange={(e) => updatePengukuranUdaraRow(row.id, 'lel', e.target.value)} className="w-full p-1 border rounded" placeholder="0-10%" /></td>
                            <td className="border p-1"><input type="text" value={row.h2s} onChange={(e) => updatePengukuranUdaraRow(row.id, 'h2s', e.target.value)} className="w-full p-1 border rounded" placeholder="<10ppm" /></td>
                            <td className="border p-1"><input type="text" value={row.co} onChange={(e) => updatePengukuranUdaraRow(row.id, 'co', e.target.value)} className="w-full p-1 border rounded" /></td>
                            <td className="border p-1"><input type="text" value={row.toxic} onChange={(e) => updatePengukuranUdaraRow(row.id, 'toxic', e.target.value)} className="w-full p-1 border rounded" /></td>
                            <td className="border p-1 text-center"><button onClick={() => deletePengukuranUdaraRow(row.id)} className="text-red-500"><Trash2 size={14} /></button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <button onClick={addPengukuranUdaraRow} className="mt-2 text-blue-600 text-sm flex items-center gap-1"><Plus size={14} /> Tambah Baris</button>
                  </div>
                </div>

                {/* Section 14: Daftar Entrants */}
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">14. Daftar Entrants dan Standby Personel Confined Space</h4>
                  
                  <div className="overflow-x-auto mb-4">
                    <table className="w-full border-collapse border border-gray-300 text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="border p-2">Nama Entrants</th>                          
                          <th className="border p-2">Nama Standby Personnel</th>                          
                          <th className="border p-2 w-16">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entrantsTable.map(row => (
                          <tr key={row.id}>
                            <td className="border p-1"><input type="text" value={row.nama_entrants} onChange={(e) => updateEntrantsRow(row.id, 'nama_entrants', e.target.value)} className="w-full p-1 border rounded text-xs" /></td>
                            <td className="border p-1"><input type="text" value={row.nama_standby} onChange={(e) => updateEntrantsRow(row.id, 'nama_standby', e.target.value)} className="w-full p-1 border rounded text-xs" /></td>
                            <td className="border p-1 text-center"><button onClick={() => deleteEntrantsRow(row.id)} className="text-red-500"><Trash2 size={16} /></button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <button onClick={addEntrantsRow} className="mt-2 text-blue-600 text-sm flex items-center gap-1"><Plus size={14} /> Tambah Baris</button>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium text-gray-700">Apakah Emergency Procedure dan Penyelamatan sudah dimengerti? <span className="text-red-500">*</span></p>
                    <div className="flex gap-4 mt-1">
                      <label className="flex items-center gap-2"><input type="radio" name="emergency" value="Ya" checked={emergencyProcedure === 'Ya'} onChange={() => { setEmergencyProcedure('Ya'); setFormData(prev => ({ ...prev, high_risk_forms: { ...prev.high_risk_forms, confined_space: { ...prev.high_risk_forms.confined_space, emergency_procedure: 'Ya' } } })); }} className="w-4 h-4" /><span>Ya</span></label>
                      <label className="flex items-center gap-2"><input type="radio" name="emergency" value="Tidak" checked={emergencyProcedure === 'Tidak'} onChange={() => { setEmergencyProcedure('Tidak'); setFormData(prev => ({ ...prev, high_risk_forms: { ...prev.high_risk_forms, confined_space: { ...prev.high_risk_forms.confined_space, emergency_procedure: 'Tidak' } } })); }} className="w-4 h-4" /><span>Tidak</span></label>
                      <label className="flex items-center gap-2"><input type="radio" name="emergency" value="Perlu Briefing" checked={emergencyProcedure === 'Perlu Briefing'} onChange={() => { setEmergencyProcedure('Perlu Briefing'); setFormData(prev => ({ ...prev, high_risk_forms: { ...prev.high_risk_forms, confined_space: { ...prev.high_risk_forms.confined_space, emergency_procedure: 'Perlu Briefing' } } })); }} className="w-4 h-4" /><span>Perlu Briefing</span></label>
                    </div>
                    {validationErrors.emergency_procedure && <p className="text-red-500 text-xs mt-1">{validationErrors.emergency_procedure}</p>}
                  </div>
                </div>

                {/* Section 15: QSHE PROCEDURE */}
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-3">15. QSHE PROCEDURE</h4>
                  
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Procedure yang menjadi Guidelines pekerja <span className="text-red-500">*</span></p>
                    <div className="flex flex-wrap gap-4">
                      {[
                        { value: 'ULI', label: 'Procedure Internal Flora' },
                        { value: 'OSHA', label: 'OSHA' },
                        { value: 'HSE', label: 'HSE' },
                        { value: 'Depnaker Indonesia', label: 'Kemnaker' },
                        { value: 'Lainnya', label: 'Lainnya' }
                      ].map(opt => (
                        <label key={opt.value} className="flex items-center gap-2">
                          <input type="checkbox" checked={qsheProcedure.guidelines.includes(opt.value)} onChange={() => updateQsheGuidelines(opt.value)} className="w-4 h-4" />
                          <span className="text-sm">{opt.label}</span>
                        </label>
                      ))}
                    </div>
                    {validationErrors.qshe_guidelines && <p className="text-red-500 text-xs mt-1">{validationErrors.qshe_guidelines}</p>}
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nama Procedure <span className="text-red-500">*</span></label>
                    <input type="text" value={qsheProcedure.nama_procedure} onChange={(e) => updateQsheNamaProcedure(e.target.value)} className={`w-full p-2 border rounded text-sm ${validationErrors.qshe_nama_procedure ? 'border-red-500 bg-red-50' : 'border-gray-300'}`} placeholder="Masukkan nama procedure" />
                    {validationErrors.qshe_nama_procedure && <p className="text-red-500 text-xs mt-1">{validationErrors.qshe_nama_procedure}</p>}
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Risk Assessment (RA) yang dipakai <span className="text-red-500">*</span></p>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2"><input type="radio" name="risk_assessment" value="RA yang sudah ada" checked={qsheProcedure.risk_assessment === 'RA yang sudah ada'} onChange={() => updateQsheRiskAssessment('RA yang sudah ada')} className="w-4 h-4" /><span>RA yang sudah ada</span></label>
                      <label className="flex items-center gap-2"><input type="radio" name="risk_assessment" value="RA Tambahan" checked={qsheProcedure.risk_assessment === 'RA Tambahan'} onChange={() => updateQsheRiskAssessment('RA Tambahan')} className="w-4 h-4" /><span>RA Tambahan</span></label>
                      <label className="flex items-center gap-2"><input type="radio" name="risk_assessment" value="RA yang dibuat sebelum pekerjaan" checked={qsheProcedure.risk_assessment === 'RA yang dibuat sebelum pekerjaan'} onChange={() => updateQsheRiskAssessment('RA yang dibuat sebelum pekerjaan')} className="w-4 h-4" /><span>RA yang dibuat sebelum pekerjaan</span></label>
                    </div>
                    {validationErrors.qshe_risk_assessment && <p className="text-red-500 text-xs mt-1">{validationErrors.qshe_risk_assessment}</p>}
                  </div>
                </div>

                {/* Declaration - MANDATORY */}
                <div className="bg-yellow-50 border-2 border-yellow-400 p-4 rounded-lg">
                  <h4 className="font-semibold text-yellow-800 mb-3">DEKLARASI WAJIB</h4>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.high_risk_forms.confined_space.declaration_understand_work}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          high_risk_forms: {
                            ...formData.high_risk_forms,
                            confined_space: {
                              ...formData.high_risk_forms.confined_space,
                              declaration_understand_work: e.target.checked
                            }
                          }
                        });
                      }}
                      className="w-5 h-5 mt-0.5 rounded border-yellow-500"
                    />
                    <span className="text-sm text-yellow-800 font-medium">
                      Saya menyatakan bahwa saya memahami semua pekerjaan confined space, risiko yang terlibat, 
                      dan langkah-langkah keselamatan yang harus diikuti. <span className="text-red-500">*</span>
                    </span>
                  </label>
                  {validationErrors.cs_declaration && (
                    <p className="text-xs text-red-600 mt-2">⚠️ {validationErrors.cs_declaration}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        
        <button
  type="button"
  onClick={handleSubmitHighRiskForms}
  disabled={loading}
  className="w-full py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-400 flex items-center justify-center gap-2"
>
          {loading ? (
            <><span className="animate-spin">⏳</span> Submitting...</>
          ) : (
            <><CheckCircle size={20} /> Submit High-Risk Forms</>
          )}
        </button>
      </div>
    );
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <div className="max-w-5xl mx-auto p-6">
          <div className="mb-6">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
            >
              <ArrowLeft size={20} />
              Kembali
            </button>
            <h1 className="text-3xl font-bold text-gray-800">
              {isFillingHighRiskForms ? 'Isi Form High Risk Detail' : isEditMode ? 'Edit PTW' : 'PTW BARU'}
            </h1>
          </div>

          {!isFillingHighRiskForms && (
            <div className="mb-8">
              <div className="flex items-center justify-between">
                {steps.map((step, index) => (
                  <div key={step} className="flex-1 flex items-center">
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                          index === currentStep
                            ? 'bg-blue-600 text-white'
                            : index < currentStep
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-300 text-gray-600'
                        }`}
                      >
                        {index < currentStep ? '✓' : index + 1}
                      </div>
                      <span className="text-xs mt-2 text-center">{step}</span>
                    </div>
                    {index < steps.length - 1 && (
                      <div
                        className={`flex-1 h-1 mx-2 ${
                          index < currentStep ? 'bg-green-600' : 'bg-gray-300'
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Error Summary */}
          {Object.keys(validationErrors).length > 0 && !isFillingHighRiskForms && !isClosingMode && (
            <ValidationErrorSummary errors={validationErrors} />
          )}

          <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
            {renderStepContent()}
          </div>

          {/* Navigation buttons for closing mode */}
          {isClosingMode && (
            <div className="flex justify-end">
              <button
                onClick={handleSubmitClosing}
                disabled={loading}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? 'Submitting...' : (
                  <>
                    <CheckCircle size={20} /> Submit Closing
                  </>
                )}
              </button>
            </div>
          )}

          {!isFillingHighRiskForms && !isClosingMode && (
            <div className="flex justify-between">
              <button
                onClick={handlePrev}
                disabled={currentStep === 0}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <ChevronLeft size={20} /> Previous
              </button>
              {currentStep < steps.length - 1 && (
                <button
                  onClick={handleNext}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  Next <ChevronRight size={20} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PTWForm;